/**
 * @fileoverview Create Job Dialog
 *
 * Purpose: Modal dialog for creating a new investigation job. Wizard:
 * kind (pre/post-appointment) → case type → document upload (AI-extracted
 * to pre-fill the form) → review. The review form is split into tab
 * panels (MOR-998): Job Details, Referral (MOR-996), Team, Documents
 * (bulk attach, MOR-997), Document Variants/Tags, and Notes. On submit,
 * the NOA file and any bulk-attached documents are persisted as
 * `documents` rows scoped to the new job.
 *
 * Dependencies:
 * - shadcn Dialog, Input, Select, Button, Label, Badge, Checkbox, Textarea
 * - Convex: jobs.mutations.createJob, organizations.queries.getOrganizationMembers
 *           jobs.noticeOfAppointment.mutations.generateNoaUploadUrl,
 *           jobs.noticeOfAppointment.actions.extractNoaFields
 * - ABR lookup: abn.actions.searchByABN, searchByACN, searchByName
 * - Active org context
 *
 * Used by:
 * - Jobs list page (New Job button)
 * - Dashboard (New Job button)
 */

import {
  useState,
  useEffect,
  useRef,
  useCallback,
  type ReactNode,
} from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/ui/composed/overlays/dialog";
import { Button } from "@/ui/primitives/button";
import { Input } from "@/ui/primitives/input";
import { Label } from "@/ui/primitives/label";
import { Textarea } from "@/ui/primitives/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/primitives/select";
import { Checkbox } from "@/ui/primitives/checkbox";
import {
  ArrowLeft,
  ArrowRight,
  Briefcase,
  Building2,
  Check,
  ClipboardList,
  FileText,
  Handshake,
  Layers,
  LayoutList,
  Loader2,
  Plus,
  RotateCcw,
  Search,
  Sparkles,
  StickyNote,
  Tag,
  Upload,
  User,
  Users,
  X,
  ChevronDown,
  Info,
  type LucideIcon,
} from "lucide-react";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/ui/composed/sections/tabs";
import { SearchableSelect } from "@/ui/composed/inputs/searchable-select";
import { AddReferrerDialog } from "@/components/pre-appointment/add-referrer-dialog";
import { parseAustralianAddress } from "@/lib/australian-address";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/ui/composed/sections/collapsible";
import { ANZSIC_CODES } from "@/data/anzsic-codes";
import { DatePicker } from "@/ui/composed/inputs/date-picker";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  convexQuery,
  useConvexAction,
  useConvexMutation,
} from "@convex-dev/react-query";
import { ConvexError } from "convex/values";
import { api } from "@cvx/_generated/api";
import { useActiveOrg } from "@/utils/org-context";
import { useNavigate } from "@tanstack/react-router";
import { toast } from "sonner";
import { getDisplayName, UserAvatar } from "@/ui/composed";
import { JobRoleSelect } from "@/components/jobs/JobRoleSelect";
import { WorkflowRoleSelect } from "@/components/settings/tasks/WorkflowRoleSelect";
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from "@/ui/composed/data-display";
import { cn } from "@/utils/misc";
import type {
  AppointmentType,
  CompanyNameVariant,
  CompanyNameVariantType,
  JobRole,
  WindingUpApplicationStatus,
} from "@cvx/jobs/schema";
import type { Id } from "@cvx/_generated/dataModel";
import { RelationBackFieldsSection } from "./-ui.job-relation-back-fields";
import { titleCaseCompanyName } from "@cvx/lib/companyName";
import {
  caseTypeFromAppointmentType,
  CORPORATE_APPOINTMENT_TYPES,
  PERSONAL_APPOINTMENT_TYPES,
  type CaseType,
} from "@/lib/precedent-templates";

// ---------------------------------------------------------------------------
// Constants & localStorage draft persistence
// ---------------------------------------------------------------------------

const STORAGE_KEY = "morgan_create_job_draft";

/**
 * Job creation kind. `pre_appointment` corresponds to Flow A (firm not
 * yet appointed — pitching / pre-engagement). `post_appointment` is the
 * standard Flow B (firm already appointed at intake).
 */
export type JobCreationKind = "pre_appointment" | "post_appointment";

/**
 * MOR-429 day-one fields — court / bankruptcy scalars surfaced by NOA
 * extraction or typed on the personal intake form. Empty/undefined values
 * are filtered before submit.
 */
interface DayOneFields {
  courtName?: string;
  courtState?: string;
  courtNumber?: string;
  sequestrationNumber?: string;
  proceedingMatterNumber?: string;
  proceedingYear?: string;
  courtOrderDate?: string;
  windingUpApplicationDate?: string;
  administrationStartDate?: string;
  plaintiffName?: string;
  defendantName?: string;
  bankruptName?: string;
  /** MOR-1847 — ISO date; identifies the individual on the conflict check. */
  bankruptDateOfBirth?: string;
  administrationNumber?: string;
  petitioningCreditorCosts?: string;
  actOfBankruptcyDate?: string;
}

interface CreateJobDraft {
  /** Pre vs post-appointment kind chosen on Form 1. */
  kind?: JobCreationKind;
  /**
   * Corporate vs Personal Insolvency case type chosen on Form 1 (MOR-470).
   * Derived value, not persisted on the job — it only drives the intake
   * form's labels, fields, and validation.
   */
  caseType?: CaseType;
  jobName: string;
  /**
   * MOR-1858 (QA-06) — only persisted once the practitioner has edited it;
   * an untouched code keeps following the firm's next suggestion instead.
   */
  jobCode?: string;
  entityName: string;
  acn: string;
  abn: string;
  /**
   * MOR-340 trust handling. `isTrustee` controls whether the trust section
   * is visible (toggled either manually or automatically when ABR signals
   * trustee involvement). `trustAbn` / `trustName` are filled via a second
   * ABR lookup once the user enters the trust ABN.
   */
  isTrustee?: boolean;
  trustAbn?: string;
  trustName?: string;
  registeredAddress: string;
  registeredOfficeStreet: string;
  registeredOfficeSuburb: string;
  registeredOfficeState: string;
  registeredOfficePostcode: string;
  principalPlaceStreet: string;
  principalPlaceSuburb: string;
  principalPlaceState: string;
  principalPlacePostcode: string;
  principalPlaceSameAsRegistered: boolean;
  companyNameVariants?: CompanyNameVariant[];
  anzsicCode: string;
  anzsicReasoning: string;
  appointmentType: AppointmentType | "";
  appointmentDate: string;
  notes: string;
  addedMembers: Array<{
    id: string;
    userId?: string;
    name: string;
    email: string;
    isAppointee: boolean;
    source: "org" | "external";
    /** MOR-867 — job-level role for this member (absent = member). */
    role?: JobRole;
  }>;
  /** MOR-748 — workflow role id → selected user id. */
  workflowRoleAssignments?: Record<string, string>;
  /** MOR-996 — referrer picked on the creation form. */
  referrerFirmContactId?: string;
  /**
   * MOR-1858 (QA-06) — the bankrupt's name and date of birth, plus any
   * court fields typed or extracted, so a personal draft survives a reload.
   */
  dayOneFields?: DayOneFields;
  windingUpApplicationStatus?: WindingUpApplicationStatus;
}

/**
 * Whether a draft holds anything worth restoring. Shared by the autosave and
 * the close handler so both agree on what counts as "started a job".
 */
function hasDraftContent(draft: CreateJobDraft): boolean {
  return Boolean(
    draft.jobName ||
    draft.jobCode ||
    draft.entityName ||
    draft.acn ||
    draft.abn ||
    draft.trustAbn ||
    draft.trustName ||
    draft.registeredAddress ||
    draft.registeredOfficeStreet ||
    draft.anzsicCode ||
    draft.appointmentType ||
    draft.appointmentDate ||
    draft.notes ||
    draft.addedMembers.length > 0 ||
    (draft.companyNameVariants?.length ?? 0) > 0 ||
    Object.values(draft.dayOneFields ?? {}).some(Boolean),
  );
}

/**
 * Auto-fill source flags. When a field was populated by NOA extraction we
 * surface a small "Auto-filled from NOA" badge so the user knows what the
 * AI inferred. The flag is cleared the moment the user edits the field.
 */
type AutofillFlags = Partial<
  Record<
    | "jobName"
    | "entityName"
    | "acn"
    | "abn"
    | "registeredAddress"
    | "registeredOfficeStreet"
    | "registeredOfficeSuburb"
    | "registeredOfficeState"
    | "registeredOfficePostcode"
    | "appointmentType"
    | "appointmentDate"
    | "notes",
    boolean
  >
>;

function saveDraft(draft: CreateJobDraft) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(draft));
  } catch {
    // localStorage may be full or unavailable — silently ignore
  }
}

function loadDraft(): CreateJobDraft | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as CreateJobDraft;
  } catch {
    return null;
  }
}

function clearDraft() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // noop
  }
}

const APPOINTMENT_TYPE_LABELS: Record<string, string> = {
  cvl: "Creditors Voluntary Liquidation (CVL)",
  mvl: "Members Voluntary Liquidation (MVL)",
  simplified_liquidation: "Simplified Liquidation",
  court_liquidation: "Court Liquidation",
  provisional_liquidation: "Provisional Liquidation",
  sbr: "Small Business Restructuring (SBR)",
  voluntary_administration: "Voluntary Administration (VA)",
  deed_of_company_arrangement: "Deed of Company Arrangement (DOCA)",
  receivership: "Receivership",
  scheme_of_arrangement: "Scheme of Arrangement",
  personal_bankruptcy: "Personal Bankruptcy",
  business_bankruptcy: "Business Bankruptcy",
  creditors_petition: "Creditors Petition",
  debtors_petition: "Debtors Petition",
  part_x_arrangement: "Personal Insolvency Agreement (Part X)",
};

const COMPANY_NAME_VARIANT_TYPES: CompanyNameVariantType[] = [
  "trading_name",
  "trust_name",
  "former_trading_name",
];

const COMPANY_NAME_VARIANT_TYPE_LABELS: Record<CompanyNameVariantType, string> =
  {
    trading_name: "Trading name",
    trust_name: "Trust name",
    former_trading_name: "Former trading name",
  };

function normaliseCompanyNameVariants(
  variants: CompanyNameVariant[],
): CompanyNameVariant[] {
  const seen = new Set<string>();

  return variants.flatMap((variant) => {
    const name = variant.name.trim();
    if (!name) return [];

    const key = `${variant.type}:${name.toLowerCase()}`;
    if (seen.has(key)) return [];
    seen.add(key);

    return [
      {
        ...variant,
        name,
      },
    ];
  });
}

// ---------------------------------------------------------------------------
// Review-step tabs (MOR-998)
// ---------------------------------------------------------------------------

type ReviewTab =
  | "all"
  | "details"
  | "referral"
  | "team"
  | "documents"
  | "variants"
  | "tags"
  | "notes";

/**
 * Tab order, labels and icons for the review step. `all` stacks every
 * section on one scrolling page (with a heading per section); the rest
 * show a single section. Variants/tags are only offered when the firm has
 * groups configured.
 */
const REVIEW_TABS: ReadonlyArray<{
  id: ReviewTab;
  /** Short label for the tab strip — must fit eight tabs on one row. */
  label: string;
  /** Full heading shown above the section in All mode. */
  heading: string;
  icon: LucideIcon;
}> = [
  { id: "all", label: "All", heading: "All", icon: LayoutList },
  {
    id: "details",
    label: "Details",
    heading: "Job Details",
    icon: ClipboardList,
  },
  { id: "referral", label: "Referral", heading: "Referral", icon: Handshake },
  { id: "team", label: "Team", heading: "Team", icon: Users },
  { id: "documents", label: "Documents", heading: "Documents", icon: FileText },
  {
    id: "variants",
    label: "Variants",
    heading: "Document Variants",
    icon: Layers,
  },
  { id: "tags", label: "Tags", heading: "Document Tags", icon: Tag },
  { id: "notes", label: "Notes", heading: "Notes", icon: StickyNote },
];

// ---------------------------------------------------------------------------
// ABR lookup hook
// ---------------------------------------------------------------------------

function useAbrLookup() {
  const { mutateAsync: lookupByABN, isPending: isAbnLoading } = useMutation({
    mutationFn: useConvexAction(api.abn.actions.searchByABN),
  });
  const { mutateAsync: lookupByACN, isPending: isAcnLoading } = useMutation({
    mutationFn: useConvexAction(api.abn.actions.searchByACN),
  });
  const { mutateAsync: searchByName, isPending: isNameLoading } = useMutation({
    mutationFn: useConvexAction(api.abn.actions.searchByName),
  });
  const { mutateAsync: guessAnzsic, isPending: isAnzsicGuessing } = useMutation(
    {
      mutationFn: useConvexAction(api.abn.actions.guessAnzsicCode),
    },
  );

  return {
    lookupByABN,
    lookupByACN,
    searchByName,
    guessAnzsic,
    isAbnLoading,
    isAcnLoading,
    isNameLoading,
    isAnzsicGuessing,
  };
}

// ---------------------------------------------------------------------------
// Debounce hook
// ---------------------------------------------------------------------------

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);
  return debounced;
}

// ---------------------------------------------------------------------------
// ABN/ACN formatting helpers
// ---------------------------------------------------------------------------

/** Strip non-digit characters */
function digitsOnly(s: string) {
  return s.replace(/\D/g, "");
}

/** Format ABN as "XX XXX XXX XXX" */
function formatABN(raw: string): string {
  const d = digitsOnly(raw).slice(0, 11);
  if (d.length <= 2) return d;
  if (d.length <= 5) return `${d.slice(0, 2)} ${d.slice(2)}`;
  if (d.length <= 8) return `${d.slice(0, 2)} ${d.slice(2, 5)} ${d.slice(5)}`;
  return `${d.slice(0, 2)} ${d.slice(2, 5)} ${d.slice(5, 8)} ${d.slice(8)}`;
}

/** Format ACN as "XXX XXX XXX" */
function formatACN(raw: string): string {
  const d = digitsOnly(raw).slice(0, 9);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `${d.slice(0, 3)} ${d.slice(3)}`;
  return `${d.slice(0, 3)} ${d.slice(3, 6)} ${d.slice(6)}`;
}

// ---------------------------------------------------------------------------
// Name search result type
// ---------------------------------------------------------------------------

interface NameSearchResult {
  abn: string;
  name: string;
  nameType: "main" | "trading" | "other";
  score: number;
  state: string;
  postcode: string;
  status: string;
}

interface OrgMemberItem {
  _id: string;
  role: string;
  user: {
    _id: string;
    email: string;
    firstName?: string;
    lastName?: string;
    avatarUrl?: string;
  };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface CreateJobDialogProps {
  /** Trigger element (e.g., a button) */
  children: ReactNode;
}

/**
 * Sheet dialog for creating a new job.
 *
 * @example
 * <CreateJobDialog>
 *   <Button>New Job</Button>
 * </CreateJobDialog>
 */
export function CreateJobDialog({ children }: CreateJobDialogProps) {
  const [open, setOpen] = useState(false);
  const { orgId, activeOrg } = useActiveOrg();
  const navigate = useNavigate();
  // Firm-directory writes (adding a referrer contact) are owner/admin only,
  // mirroring the pre-appointment pipeline surface.
  const canCreateReferrer =
    activeOrg?.role === "owner" || activeOrg?.role === "admin";

  // Fetch org members to populate practitioners list.
  // The orgId cast is safe because enabled guards against null.
  const { data: orgMembers } = useQuery({
    ...convexQuery(api.organizations.queries.getOrganizationMembers, {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      orgId: orgId!,
    }),
    enabled: !!orgId && open,
  });

  const { data: tagGroups } = useQuery({
    ...convexQuery(api.settings.precedents.tagGroups.listTagGroups, {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      orgId: orgId!,
    }),
    enabled: !!orgId && open,
  });

  // Firm's next suggested job code, to pre-fill the editable field below.
  const { data: jobCodeSettings } = useQuery({
    ...convexQuery(api.settings.jobCode.queries.getJobCodeSettings, {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      orgId: orgId!,
    }),
    enabled: !!orgId && open,
  });
  const suggestedJobCode = jobCodeSettings
    ? `${jobCodeSettings.prefix || "J-"}${String(
        jobCodeSettings.nextNumber,
      ).padStart(4, "0")}`
    : "";

  const { data: variantGroups } = useQuery({
    ...convexQuery(api.settings.precedents.variantGroups.listVariantGroups, {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      orgId: orgId!,
    }),
    enabled: !!orgId && open,
  });

  // MOR-722 — org teams + the org's default job access, driving the
  // team / all-staff selection in the Team section below.
  const { data: orgTeams } = useQuery({
    ...convexQuery(api.organizations.teams.listTeams, {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      orgId: orgId!,
    }),
    enabled: !!orgId && open,
  });

  const { data: jobAccessDefault } = useQuery({
    ...convexQuery(api.organizations.teams.getJobAccessDefault, {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      orgId: orgId!,
    }),
    enabled: !!orgId && open,
  });

  // MOR-748 — the firm's workflow roles, driving the "Workflow roles"
  // assignment block in the Team section below.
  const { data: workflowRoles } = useQuery({
    ...convexQuery(api.tasks.workflowRoles.listByOrg, {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      orgId: orgId!,
    }),
    enabled: !!orgId && open,
  });

  // MOR-996 — active referrer contacts for the Referral tab's picker.
  const { data: referrers } = useQuery({
    ...convexQuery(api.preAppointment.pipeline.listReferrers, {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      orgId: orgId!,
    }),
    enabled: !!orgId && open,
  });

  // Live ref to org members so the NOA extraction callback (which uses
  // a stable [] dep array) always sees the latest list when matching
  // extracted practitioner names.
  const orgMembersRef = useRef<OrgMemberItem[] | undefined>(undefined);
  useEffect(() => {
    orgMembersRef.current = orgMembers as OrgMemberItem[] | undefined;
  }, [orgMembers]);

  // Live ref to the active org id, for the same reason as orgMembersRef — the
  // NOA extraction callback has a stable [] dep array and reads it at call time.
  const orgIdRef = useRef<typeof orgId>(orgId);
  useEffect(() => {
    orgIdRef.current = orgId;
  }, [orgId]);

  // Wizard step. `kind` is the pre vs post chooser; `caseType` picks
  // corporate vs personal insolvency; `upload` is the optional NOA upload
  // (post-appointment only); `review` is the main intake form.
  const [step, setStep] = useState<"kind" | "caseType" | "upload" | "review">(
    "kind",
  );
  const [jobKind, setJobKind] = useState<JobCreationKind | null>(null);
  const isPreAppointment = jobKind === "pre_appointment";
  // Live ref for the same reason as orgMembersRef / orgIdRef below — the
  // extraction callback has a stable [] dep array and needs the current
  // kind at call time to decide which extracted fields to apply.
  const isPreAppointmentRef = useRef(isPreAppointment);
  useEffect(() => {
    isPreAppointmentRef.current = isPreAppointment;
  }, [isPreAppointment]);
  // MOR-470 — Corporate vs Personal Insolvency. UI-only state; the persisted
  // signal is `appointmentType` (the two bankruptcy types ARE the personal
  // cases). Drives field labels, which appointment types are offered, and
  // validation.
  const [caseType, setCaseType] = useState<CaseType>("corporate");
  const isPersonal = caseType === "personal";
  // Live ref for the extraction callback (stable [] deps) — it needs the
  // chosen case type at call time so a personal upload reads the individual
  // as the subject and never fills company identifiers.
  const caseTypeRef = useRef(caseType);
  useEffect(() => {
    caseTypeRef.current = caseType;
  }, [caseType]);

  // Form state
  const [jobName, setJobName] = useState("");
  // Job code sent to InfoTrack as the client reference. Pre-filled with the
  // firm's next suggested code (editable); blank falls back to the auto-mint.
  const [jobCode, setJobCode] = useState("");
  const [jobCodeEdited, setJobCodeEdited] = useState(false);
  // Seed the field with the firm's suggested code until the user edits it.
  useEffect(() => {
    if (!jobCodeEdited && suggestedJobCode) setJobCode(suggestedJobCode);
  }, [suggestedJobCode, jobCodeEdited]);
  const [entityName, setEntityName] = useState("");
  const [acn, setAcn] = useState("");
  const [abn, setAbn] = useState("");
  // MOR-340 — trust fields. `isTrustee` is the user-visible toggle that
  // reveals the trust ABN section. It can be flipped automatically when the
  // company ACN lookup returns no ABN (a strong hint the company only acts
  // as trustee), but the user always stays in control of the final state.
  const [isTrustee, setIsTrustee] = useState(false);
  const [trustAbn, setTrustAbn] = useState("");
  const [trustName, setTrustName] = useState("");
  /**
   * Set when the most recent company ACN lookup returned an entity with no
   * ABN. We surface a one-time hint suggesting the user enable the trust
   * section — the company may operate solely as trustee.
   */
  const [companyHasNoAbnHint, setCompanyHasNoAbnHint] = useState(false);
  const [registeredAddress, setRegisteredAddress] = useState("");
  const [registeredOfficeStreet, setRegisteredOfficeStreet] = useState("");
  const [registeredOfficeSuburb, setRegisteredOfficeSuburb] = useState("");
  const [registeredOfficeState, setRegisteredOfficeState] = useState("");
  const [registeredOfficePostcode, setRegisteredOfficePostcode] = useState("");
  const [principalPlaceStreet, setPrincipalPlaceStreet] = useState("");
  const [principalPlaceSuburb, setPrincipalPlaceSuburb] = useState("");
  const [principalPlaceState, setPrincipalPlaceState] = useState("");
  const [principalPlacePostcode, setPrincipalPlacePostcode] = useState("");
  const [principalPlaceSameAsRegistered, setPrincipalPlaceSameAsRegistered] =
    useState(true);
  const [companyNameVariants, setCompanyNameVariants] = useState<
    CompanyNameVariant[]
  >([]);
  const [anzsicCode, setAnzsicCode] = useState("");
  const [anzsicSearch, setAnzsicSearch] = useState("");
  const [anzsicDropdownOpen, setAnzsicDropdownOpen] = useState(false);
  const [anzsicReasoning, setAnzsicReasoning] = useState("");
  const [appointmentType, setAppointmentType] = useState<AppointmentType | "">(
    "",
  );
  const [appointmentDate, setAppointmentDate] = useState("");
  const [notes, setNotes] = useState("");
  const [notesExpanded, setNotesExpanded] = useState(false);
  const [addedMembers, setAddedMembers] = useState<
    Array<{
      id: string;
      userId?: string;
      name: string;
      email: string;
      isAppointee: boolean;
      source: "org" | "external";
      /** MOR-867 — job-level role for this member (absent = member). */
      role?: JobRole;
    }>
  >([]);
  // MOR-748 — who fills each workflow role on the new job (roleId → userId).
  // Pre-filled from team member defaults when a team is selected; always
  // editable before submit.
  const [workflowRoleSelections, setWorkflowRoleSelections] = useState<
    Record<string, string>
  >({});
  const [teamSearch, setTeamSearch] = useState("");
  const [teamDropdownOpen, setTeamDropdownOpen] = useState(false);
  const [customTagSelections, setCustomTagSelections] = useState<
    Record<string, string[]>
  >({});
  const [variantSelections, setVariantSelections] = useState<
    Record<string, string>
  >({});

  // MOR-998 — the review form is broken into real tab panels (previously a
  // scroll-spy nav whose highlight could point at sections that weren't
  // rendered). Controlled so validation can jump to the offending tab.
  const [activeSection, setActiveSection] = useState<ReviewTab>("all");

  // MOR-996 — referrer picked on the creation form (pre-appointment
  // pipeline referrer, same contact set as the pipeline panel's picker).
  const [referrerId, setReferrerId] = useState<Id<"firmContacts"> | null>(null);
  const [addReferrerOpen, setAddReferrerOpen] = useState(false);

  // MOR-997 — bulk document dump. Files uploaded to storage as they are
  // picked; the storage ids are handed to createJob on submit.
  const [extraDocs, setExtraDocs] = useState<
    Array<{
      id: string;
      fileName: string;
      fileType: string;
      fileSize: number;
      storageId: Id<"_storage"> | null;
      phase: "uploading" | "done" | "error";
      error?: string;
    }>
  >([]);
  const extraDocsInputRef = useRef<HTMLInputElement>(null);

  // MOR-429 day-one fields, held as a single object so the create call can
  // spread them straight into createJob without 13 individual setters.
  const [dayOneFields, setDayOneFields] = useState<DayOneFields>({});

  // MOR-901 — answer to "was a winding-up application already filed?". Kept
  // out of `dayOneFields` because it is a status enum rather than a string, and
  // `undefined` (unanswered) is meaningfully different from "none" (answered
  // no) — only the latter is recorded on the job.
  const [windingUpStatus, setWindingUpStatus] = useState<
    WindingUpApplicationStatus | undefined
  >(undefined);

  // MOR-470 — manual editor for a single day-one/court field. Used by the
  // Personal Insolvency intake inputs (court reference, bankrupt name) which
  // write straight into the same `dayOneFields` object that NOA extraction
  // populates. Empty strings are stored as `undefined` so they're dropped at
  // submit rather than overwriting with blanks.
  const setDayOneField = useCallback(
    (key: keyof typeof dayOneFields, value: string) => {
      setDayOneFields((prev) => ({ ...prev, [key]: value || undefined }));
    },
    [],
  );

  // Notice of Appointment upload state
  const [noaFile, setNoaFile] = useState<File | null>(null);
  const [noaStorageId, setNoaStorageId] = useState<Id<"_storage"> | null>(null);
  const [noaPhase, setNoaPhase] = useState<
    "idle" | "uploading" | "extracting" | "done" | "error"
  >("idle");
  const [noaError, setNoaError] = useState("");
  const [autofill, setAutofill] = useState<AutofillFlags>({});
  const noaInputRef = useRef<HTMLInputElement>(null);

  // ABR lookup state
  const abr = useAbrLookup();
  const [nameSearch, setNameSearch] = useState("");
  const [nameResults, setNameResults] = useState<NameSearchResult[]>([]);
  const [nameDropdownOpen, setNameDropdownOpen] = useState(false);
  const [nameSearchEmpty, setNameSearchEmpty] = useState(false);
  const [abrPopulated, setAbrPopulated] = useState(false);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const debouncedNameSearch = useDebouncedValue(nameSearch, 200);

  // Track whether ABN/ACN was just auto-populated (to avoid re-triggering lookup)
  const abnPopulatedRef = useRef(false);
  const acnPopulatedRef = useRef(false);
  // MOR-340 — same idea for the trust ABN field.
  const trustAbnPopulatedRef = useRef(false);

  // Restore draft from localStorage when the dialog opens
  const [draftRestored, setDraftRestored] = useState(false);
  useEffect(() => {
    if (open && !draftRestored) {
      const draft = loadDraft();
      if (draft) {
        if (draft.kind) setJobKind(draft.kind);
        if (draft.caseType) setCaseType(draft.caseType);
        setJobName(draft.jobName);
        if (draft.jobCode) {
          setJobCode(draft.jobCode);
          setJobCodeEdited(true);
        }
        setEntityName(draft.entityName);
        setAcn(draft.acn);
        setAbn(draft.abn);
        setDayOneFields(draft.dayOneFields ?? {});
        setWindingUpStatus(draft.windingUpApplicationStatus);
        setIsTrustee(draft.isTrustee ?? false);
        setTrustAbn(draft.trustAbn ?? "");
        setTrustName(draft.trustName ?? "");
        setRegisteredAddress(draft.registeredAddress);
        setRegisteredOfficeStreet(draft.registeredOfficeStreet ?? "");
        setRegisteredOfficeSuburb(draft.registeredOfficeSuburb ?? "");
        setRegisteredOfficeState(draft.registeredOfficeState ?? "");
        setRegisteredOfficePostcode(draft.registeredOfficePostcode ?? "");
        setPrincipalPlaceStreet(draft.principalPlaceStreet ?? "");
        setPrincipalPlaceSuburb(draft.principalPlaceSuburb ?? "");
        setPrincipalPlaceState(draft.principalPlaceState ?? "");
        setPrincipalPlacePostcode(draft.principalPlacePostcode ?? "");
        setPrincipalPlaceSameAsRegistered(
          draft.principalPlaceSameAsRegistered ?? true,
        );
        setCompanyNameVariants(draft.companyNameVariants ?? []);
        setAnzsicCode(draft.anzsicCode);
        setAnzsicReasoning(draft.anzsicReasoning);
        setAppointmentType(draft.appointmentType);
        setAppointmentDate(draft.appointmentDate);
        setNotes(draft.notes ?? "");
        setAddedMembers(draft.addedMembers);
        setWorkflowRoleSelections(draft.workflowRoleAssignments ?? {});
        setReferrerId(
          draft.referrerFirmContactId
            ? (draft.referrerFirmContactId as Id<"firmContacts">)
            : null,
        );
        if (draft.abn || draft.acn || draft.entityName) {
          setAbrPopulated(true);
          abnPopulatedRef.current = true;
          acnPopulatedRef.current = true;
        }
        // Resume at the right step based on draft contents:
        //  - any fields filled → go straight to review, whatever the kind
        //  - kind chosen but nothing filled → the upload step (MOR-881:
        //    pre-appointment now has one too)
        //  - no kind yet → start at the kind chooser
        const hasManualFields =
          draft.jobName ||
          draft.entityName ||
          draft.acn ||
          draft.abn ||
          draft.appointmentType ||
          draft.appointmentDate ||
          draft.dayOneFields?.bankruptName;
        if (hasManualFields) {
          setStep("review");
          setActiveSection("all");
        } else if (draft.kind) {
          setStep("upload");
        }
      }
      setDraftRestored(true);
    }
    if (!open) {
      setDraftRestored(false);
    }
  }, [open, draftRestored]);

  // MOR-722 — apply the org's default job access (all staff or a team) once
  // per dialog open by materialising those people into `addedMembers`, so
  // they show up as normal member rows with the Appointee toggle. Skipped
  // when a draft exists — the draft's member list wins. The default only
  // pre-selects; the creator can always remove people before submitting.
  const defaultAccessAppliedRef = useRef(false);
  useEffect(() => {
    if (!open) {
      defaultAccessAppliedRef.current = false;
      return;
    }
    if (
      !draftRestored ||
      !jobAccessDefault ||
      defaultAccessAppliedRef.current
    ) {
      return;
    }
    if (jobAccessDefault.defaultAccess === "none" || loadDraft()) {
      defaultAccessAppliedRef.current = true;
      return;
    }

    // Wait for the data needed to materialise the default.
    const membersList = orgMembers as OrgMemberItem[] | undefined;
    if (!membersList) return;

    if (jobAccessDefault.defaultAccess === "all_staff") {
      defaultAccessAppliedRef.current = true;
      setAddedMembers((prev) => {
        const next = [...prev];
        for (const member of membersList) {
          if (next.some((m) => m.id === member._id)) continue;
          next.push({
            id: member._id,
            userId: member.user._id,
            name: getDisplayName(
              member.user.firstName,
              member.user.lastName,
              member.user.email,
            ),
            email: member.user.email,
            isAppointee: false,
            source: "org",
          });
        }
        return next;
      });
      return;
    }

    // Team default — resolve the team's roster from listTeams. Each member
    // carries their team defaults: job role (MOR-867) and workflow role
    // (MOR-748), the latter pre-filling the role assignment block.
    if (!orgTeams) return;
    defaultAccessAppliedRef.current = true;
    const team = orgTeams.find((t) => t._id === jobAccessDefault.defaultTeamId);
    if (!team) return;
    setAddedMembers((prev) => {
      const next = [...prev];
      for (const tm of team.members) {
        if (next.some((m) => m.userId === tm.userId)) continue;
        const orgMember = membersList.find((m) => m.user._id === tm.userId);
        next.push({
          id: orgMember?._id ?? `team-${tm.userId}`,
          userId: tm.userId,
          name: getDisplayName(tm.firstName, tm.lastName, tm.email),
          email: tm.email,
          isAppointee: false,
          source: "org",
          role: tm.defaultJobRole,
        });
      }
      return next;
    });
    setWorkflowRoleSelections((prev) => {
      const next = { ...prev };
      for (const tm of team.members) {
        if (tm.defaultWorkflowRoleId && !next[tm.defaultWorkflowRoleId]) {
          next[tm.defaultWorkflowRoleId] = tm.userId;
        }
      }
      return next;
    });
  }, [open, draftRestored, jobAccessDefault, orgMembers, orgTeams]);

  // ---------------------------------------------------------------------------
  // ABR: Name / ABN / ACN search (debounced)
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (!debouncedNameSearch) {
      setNameResults([]);
      setNameSearchEmpty(false);
      return;
    }

    const stripped = debouncedNameSearch.replace(/\s/g, "");
    const isAllDigits = /^\d+$/.test(stripped);

    if (isAllDigits) {
      // ABN lookup — 11 digits
      if (stripped.length === 11) {
        let cancelled = false;
        setNameSearchEmpty(false);
        abr
          .lookupByABN({ abn: stripped })
          .then((entity) => {
            if (cancelled) return;
            if (entity) {
              setNameResults([
                {
                  abn: entity.abn,
                  name: entity.entityName,
                  nameType: "main" as const,
                  score: 100,
                  state: entity.state,
                  postcode: entity.postcode,
                  status: entity.status || "Active",
                },
              ]);
              setNameDropdownOpen(true);
              setNameSearchEmpty(false);
            } else {
              setNameResults([]);
              setNameSearchEmpty(true);
            }
          })
          .catch(() => {
            if (!cancelled) {
              setNameResults([]);
              setNameSearchEmpty(true);
            }
          });
        return () => {
          cancelled = true;
        };
      }

      // ACN lookup — 9 digits
      if (stripped.length === 9) {
        let cancelled = false;
        setNameSearchEmpty(false);
        abr
          .lookupByACN({ acn: stripped })
          .then((entity) => {
            if (cancelled) return;
            if (entity) {
              setNameResults([
                {
                  abn: entity.abn,
                  name: entity.entityName,
                  nameType: "main" as const,
                  score: 100,
                  state: entity.state,
                  postcode: entity.postcode,
                  status: entity.status || "Active",
                },
              ]);
              setNameDropdownOpen(true);
              setNameSearchEmpty(false);
            } else {
              setNameResults([]);
              setNameSearchEmpty(true);
            }
          })
          .catch(() => {
            if (!cancelled) {
              setNameResults([]);
              setNameSearchEmpty(true);
            }
          });
        return () => {
          cancelled = true;
        };
      }

      // Partial digits — not yet a valid ABN or ACN, clear results
      setNameResults([]);
      setNameSearchEmpty(false);
      return;
    }

    // Text search — requires at least 3 characters
    if (debouncedNameSearch.length < 3) {
      setNameResults([]);
      setNameSearchEmpty(false);
      return;
    }

    let cancelled = false;
    setNameSearchEmpty(false);
    abr
      .searchByName({ name: debouncedNameSearch })
      .then((results) => {
        if (!cancelled) {
          setNameResults(results);
          setNameDropdownOpen(results.length > 0);
          setNameSearchEmpty(results.length === 0);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setNameResults([]);
          setNameSearchEmpty(true);
        }
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedNameSearch]);

  // ---------------------------------------------------------------------------
  // ABR: Populate from entity lookup result
  // ---------------------------------------------------------------------------

  const applyAbrCompanyNameVariants = useCallback(
    (variants: CompanyNameVariant[] | undefined) => {
      const abrVariants = normaliseCompanyNameVariants(
        (variants ?? []).map((variant) => ({
          ...variant,
          name: titleCaseCompanyName(variant.name),
          source: "abr" as const,
        })),
      );

      setCompanyNameVariants((prev) => [
        ...prev.filter((variant) => variant.source !== "abr"),
        ...abrVariants,
      ]);
    },
    [],
  );

  const populateFromEntity = useCallback(
    (entity: {
      abn: string;
      acn: string;
      entityName: string;
      state: string;
      postcode: string;
      isTrust?: boolean;
      companyNameVariants?: CompanyNameVariant[];
    }) => {
      // MOR-340 — if ABR says this is a trust, redirect to the trust
      // section instead of overwriting the company fields. The user was
      // probably searching for the operating business name (which often
      // matches the trust name) and we want to land them in the right
      // place rather than mislabelling a trust as the appointed company.
      if (entity.isTrust) {
        // `handleSelectNameResult` and the ABN auto-lookup effect pre-fill
        // `entityName` / `abn` before we know whether the result is a trust.
        // Wipe those so the company fields aren't polluted with trust data.
        setEntityName("");
        abnPopulatedRef.current = false;
        setAbn("");
        setIsTrustee(true);
        if (entity.abn) {
          trustAbnPopulatedRef.current = true;
          setTrustAbn(formatABN(entity.abn));
        }
        setTrustName(titleCaseCompanyName(entity.entityName));
        setAbrPopulated(true);
        setNameDropdownOpen(false);
        setNameSearch("");
        toast.info(
          "Selected entity is a trust — moved to the trust section. Enter the trustee company's ACN above.",
        );
        return;
      }

      // ABR returns entity names in ALL CAPS (e.g. "EXAMPLE COMPANY PTY LTD").
      // Convert to title case so Day 1 documents render correctly. The user
      // can still edit the field if our heuristic mis-cases an initialism.
      setEntityName(titleCaseCompanyName(entity.entityName));
      if (entity.acn) {
        acnPopulatedRef.current = true;
        setAcn(formatACN(entity.acn));
      }
      if (entity.abn) {
        abnPopulatedRef.current = true;
        setAbn(formatABN(entity.abn));
        setCompanyHasNoAbnHint(false);
      } else if (entity.acn) {
        // Company has an ACN (we found it) but no ABN — strong hint it
        // only operates as trustee of a trust. Surface a one-time prompt.
        setCompanyHasNoAbnHint(true);
      }
      // ABR only provides state + postcode (not street address).
      // Pre-fill so user has a starting point — they can complete the street address.
      if (entity.state && entity.postcode) {
        setRegisteredAddress((prev) =>
          prev ? prev : `${entity.state} ${entity.postcode}`,
        );
        setRegisteredOfficeState((prev) => (prev ? prev : entity.state));
        setRegisteredOfficePostcode((prev) => (prev ? prev : entity.postcode));
      }
      applyAbrCompanyNameVariants(entity.companyNameVariants);
      setAbrPopulated(true);
      setNameDropdownOpen(false);
      setNameSearch("");
    },
    [applyAbrCompanyNameVariants],
  );

  const handleAddCompanyNameVariant = () => {
    setCompanyNameVariants((prev) => [
      ...prev,
      {
        name: "",
        type: "trading_name",
        includeInDocuments: true,
        source: "manual",
      },
    ]);
  };

  const handleUpdateCompanyNameVariant = (
    index: number,
    updates: Partial<CompanyNameVariant>,
  ) => {
    setCompanyNameVariants((prev) =>
      prev.map((variant, i) =>
        i === index ? { ...variant, ...updates } : variant,
      ),
    );
  };

  const handleRemoveCompanyNameVariant = (index: number) => {
    setCompanyNameVariants((prev) => prev.filter((_, i) => i !== index));
  };

  // ---------------------------------------------------------------------------
  // ABR: Handle name result selection → full ABN lookup
  // ---------------------------------------------------------------------------

  const handleSelectNameResult = useCallback(
    async (result: NameSearchResult) => {
      setNameDropdownOpen(false);
      setNameSearch("");

      // Immediately populate what the search result already gives us
      setEntityName(titleCaseCompanyName(result.name));
      abnPopulatedRef.current = true;
      setAbn(formatABN(result.abn));
      if (result.state && result.postcode) {
        setRegisteredAddress((prev) =>
          prev ? prev : `${result.state} ${result.postcode}`,
        );
        setRegisteredOfficeState((prev) => (prev ? prev : result.state));
        setRegisteredOfficePostcode((prev) => (prev ? prev : result.postcode));
      }
      setAbrPopulated(true);

      // Enrich in background — fetch ACN, entity type, trading name, GST
      try {
        const entity = await abr.lookupByABN({ abn: result.abn });
        if (entity) {
          populateFromEntity(entity);
        }
      } catch {
        // Already populated from the search result — no-op
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [populateFromEntity],
  );

  // ---------------------------------------------------------------------------
  // ABR: Auto-trigger ABN lookup when 11 digits are typed
  // ---------------------------------------------------------------------------

  const abnLookupFiredRef = useRef("");

  useEffect(() => {
    if (abnPopulatedRef.current) return;
    const digits = digitsOnly(abn);
    if (digits.length !== 11) return;
    if (abnLookupFiredRef.current === digits) return;
    abnLookupFiredRef.current = digits;

    let cancelled = false;
    abr
      .lookupByABN({ abn: digits })
      .then((entity) => {
        if (cancelled) return;
        if (entity) {
          populateFromEntity(entity);
        } else {
          toast.error(
            "No company found for this ABN — please check the number",
          );
        }
      })
      .catch(() => {
        if (!cancelled) {
          toast.error("ABN lookup failed — please try again");
        }
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [abn]);

  // ---------------------------------------------------------------------------
  // ABR: Auto-trigger ACN lookup when 9 digits are typed
  // ---------------------------------------------------------------------------

  const acnLookupFiredRef = useRef("");

  useEffect(() => {
    if (acnPopulatedRef.current) return;
    const digits = digitsOnly(acn);
    if (digits.length !== 9) return;
    if (acnLookupFiredRef.current === digits) return;
    acnLookupFiredRef.current = digits;

    let cancelled = false;
    abr
      .lookupByACN({ acn: digits })
      .then((entity) => {
        if (cancelled) return;
        if (entity) {
          populateFromEntity(entity);
        } else {
          toast.error(
            "No company found for this ACN — please check the number",
          );
        }
      })
      .catch(() => {
        if (!cancelled) {
          toast.error("ACN lookup failed — please try again");
        }
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [acn]);

  // ---------------------------------------------------------------------------
  // MOR-340 — Trust ABN auto-lookup. Mirrors the company ABN effect but
  // populates the trust section. We expect the looked-up entity to have
  // `isTrust === true`; if not, we still accept the value (the user may
  // have a reason) but surface a soft warning toast.
  // ---------------------------------------------------------------------------

  const trustAbnLookupFiredRef = useRef("");

  useEffect(() => {
    if (!isTrustee) return;
    if (trustAbnPopulatedRef.current) return;
    const digits = digitsOnly(trustAbn);
    if (digits.length !== 11) return;
    if (trustAbnLookupFiredRef.current === digits) return;
    trustAbnLookupFiredRef.current = digits;

    let cancelled = false;
    abr
      .lookupByABN({ abn: digits })
      .then((entity) => {
        if (cancelled) return;
        if (!entity) {
          toast.error("No entity found for this ABN — please check the number");
          return;
        }
        if (!entity.isTrust) {
          toast.warning(
            `${entity.entityName} is a ${entity.entityType || "non-trust entity"}, not a trust. Please double-check the trust ABN.`,
          );
        }
        setTrustName(titleCaseCompanyName(entity.entityName));
      })
      .catch(() => {
        if (!cancelled) {
          toast.error("Trust ABN lookup failed — please try again");
        }
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trustAbn, isTrustee]);

  // ---------------------------------------------------------------------------
  // MOR-340 — Trustee capacity derivation. `sole_trustee` when only the
  // trust ABN is present; `dual_capacity` when both ABNs are present (the
  // company may have traded in its own right too — risk flag). Returns
  // undefined when the trust section is disabled or trust ABN is empty.
  // ---------------------------------------------------------------------------

  const trusteeCapacity = (() => {
    if (!isTrustee) return undefined;
    if (!trustAbn.trim()) return undefined;
    if (abn.trim()) return "dual_capacity" as const;
    return "sole_trustee" as const;
  })();

  const showDualCapacityWarning = trusteeCapacity === "dual_capacity";

  // MOR-470 — when the case type changes, drop an appointment type that no
  // longer belongs to the selected case (e.g. switching Corporate → Personal
  // after a CVL was picked) so the dropdown never shows a stale selection.
  useEffect(() => {
    if (!appointmentType) return;
    const allowed = isPersonal
      ? PERSONAL_APPOINTMENT_TYPES
      : CORPORATE_APPOINTMENT_TYPES;
    if (!(allowed as readonly string[]).includes(appointmentType)) {
      setAppointmentType("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseType]);

  // ---------------------------------------------------------------------------
  // ANZSIC: AI suggestion via web search
  // ---------------------------------------------------------------------------

  const handleSuggestAnzsic = useCallback(async () => {
    if (!entityName) return;
    try {
      const result = await abr.guessAnzsic({
        entityName,
        acn: digitsOnly(acn) || undefined,
        abn: digitsOnly(abn) || undefined,
        validCodes: ANZSIC_CODES.map((c) => `${c.code} ${c.description}`),
      });
      setAnzsicCode(result.code);
      setAnzsicReasoning(result.reasoning);
      setAnzsicSearch("");
    } catch (err) {
      // A ConvexError carries an expected condition in its data, e.g. web
      // search being off on this deployment (MOR-2301).
      const msg =
        err instanceof ConvexError && typeof err.data === "string"
          ? err.data
          : err instanceof Error
            ? err.message
            : "ANZSIC suggestion failed";
      toast.error(msg);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityName, acn, abn]);

  // ---------------------------------------------------------------------------
  // Mutations and team management (unchanged)
  // ---------------------------------------------------------------------------

  const { mutateAsync: createJob, isPending } = useMutation({
    mutationFn: useConvexMutation(api.jobs.mutations.createJob),
  });

  // ---------------------------------------------------------------------------
  // Notice of Appointment upload + AI extraction
  // ---------------------------------------------------------------------------

  const generateNoaUploadUrl = useConvexMutation(
    api.jobs.noticeOfAppointment.mutations.generateNoaUploadUrl,
  );
  const { mutateAsync: extractNoaFields } = useMutation({
    mutationFn: useConvexAction(
      api.jobs.noticeOfAppointment.actions.extractNoaFields,
    ),
  });
  // Second pass: LLM-assisted matching of extracted practitioners/appointees to
  // org members + variant/tag suggestions, using org context the extraction
  // action doesn't have. Best-effort — failures fall back to exact-match below.
  const { mutateAsync: suggestNoaJobSetup } = useMutation({
    mutationFn: useConvexAction(
      api.jobs.noticeOfAppointment.suggest.suggestNoaJobSetup,
    ),
  });

  const handleNoaFileSelected = useCallback(
    async (file: File) => {
      const lowerName = file.name.toLowerCase();
      const isPdf =
        file.type === "application/pdf" || lowerName.endsWith(".pdf");
      const isDocx =
        file.type ===
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
        lowerName.endsWith(".docx");
      if (!isPdf && !isDocx) {
        toast.error(
          isPreAppointmentRef.current
            ? "Please upload a PDF or .docx document. Other formats aren't supported."
            : "Please upload a PDF or .docx Notice of Appointment. Other formats aren't supported.",
        );
        return;
      }
      if (file.size > 25 * 1024 * 1024) {
        toast.error("File is too large. Maximum size is 25 MB.");
        return;
      }

      setNoaFile(file);
      setNoaError("");

      // Step 1 — upload file to Convex storage
      setNoaPhase("uploading");
      const storageId = await (async (): Promise<Id<"_storage"> | null> => {
        try {
          const uploadUrl = await generateNoaUploadUrl({});
          const uploadResponse = await fetch(uploadUrl, {
            method: "POST",
            headers: { "Content-Type": file.type },
            body: file,
          });
          if (!uploadResponse.ok) throw new Error("Upload failed");
          const json = (await uploadResponse.json()) as {
            storageId: Id<"_storage">;
          };
          setNoaStorageId(json.storageId);
          return json.storageId;
        } catch (error) {
          const message =
            error instanceof Error ? error.message : "Upload failed";
          setNoaPhase("error");
          setNoaError(message);
          toast.error(message);
          return null;
        }
      })();
      if (storageId === null) return;

      // Step 2 — run AI extraction
      setNoaPhase("extracting");
      try {
        const personal = caseTypeRef.current === "personal";
        const result = await extractNoaFields({
          storageId,
          fileName: file.name,
          fileType: file.type,
          fileSize: file.size,
          orgId: orgId ?? undefined,
          caseType: caseTypeRef.current,
        });

        const flags: AutofillFlags = {};
        if (result.jobName) {
          setJobName(result.jobName);
          flags.jobName = true;
        }
        // MOR-470 — on a personal matter the subject is the individual. The
        // estate name is derived from them (mirroring the Bankrupt Name
        // field's own convenience seeding) and company identifiers are never
        // applied: any ACN/ABN in the document belongs to a company the
        // bankrupt held a role in, not to the estate.
        const bankruptName = personal
          ? (result.bankruptName ?? result.entityName)
          : undefined;
        if (personal) {
          if (bankruptName) {
            const estateName =
              result.entityName && /estate/i.test(result.entityName)
                ? result.entityName
                : `Bankrupt Estate of ${bankruptName}`;
            setEntityName(estateName);
            flags.entityName = true;
          }
        } else {
          if (result.entityName) {
            setEntityName(result.entityName);
            flags.entityName = true;
          }
          if (result.acn) {
            acnPopulatedRef.current = true;
            setAcn(formatACN(result.acn));
            flags.acn = true;
          }
          if (result.abn) {
            abnPopulatedRef.current = true;
            setAbn(formatABN(result.abn));
            flags.abn = true;
          }
        }
        if (result.registeredAddress) {
          setRegisteredAddress(result.registeredAddress);
          flags.registeredAddress = true;
          // Attempt to parse the extracted address into structured components
          const parsed = parseAustralianAddress(result.registeredAddress);
          if (parsed.street) {
            setRegisteredOfficeStreet(parsed.street);
            flags.registeredOfficeStreet = true;
          }
          if (parsed.suburb) {
            setRegisteredOfficeSuburb(parsed.suburb);
            flags.registeredOfficeSuburb = true;
          }
          if (parsed.state) {
            setRegisteredOfficeState(parsed.state);
            flags.registeredOfficeState = true;
          }
          if (parsed.postcode) {
            setRegisteredOfficePostcode(parsed.postcode);
            flags.registeredOfficePostcode = true;
          }
        }
        if (!personal && result.companyNameVariants?.length) {
          applyAbrCompanyNameVariants(result.companyNameVariants);
        }
        // The practitioner chose Corporate vs Personal explicitly on step 2,
        // so the document never overrides it. An appointment type from the
        // other regime (e.g. a CVL read off a director's company on an ASIC
        // personal search) is dropped rather than flipping the whole form.
        if (
          result.appointmentType &&
          caseTypeFromAppointmentType(result.appointmentType) ===
            caseTypeRef.current
        ) {
          setAppointmentType(result.appointmentType);
          flags.appointmentType = true;
        }
        // MOR-881 — the pre-appointment review form hides the appointment
        // date and the day-one court fields (they belong to the conversion
        // step, not intake). Skip them rather than persisting values the
        // user never had a chance to see or correct.
        const preAppointment = isPreAppointmentRef.current;
        if (result.appointmentDate && !preAppointment) {
          setAppointmentDate(result.appointmentDate);
          flags.appointmentDate = true;
        }
        if (result.notesBlock) {
          setNotes(result.notesBlock);
          flags.notes = true;
        }
        // MOR-429 day-one fields. Captured into a single state object that
        // is spread into the createJob mutation at submit time. Undefined
        // values are dropped at submit so a partial extraction doesn't
        // overwrite manual user input.
        // The bankrupt's identity IS on the pre-appointment personal form
        // (name + date of birth, MOR-1847), so it is applied for both kinds.
        const personalIdentity: Pick<
          DayOneFields,
          "bankruptName" | "bankruptDateOfBirth"
        > = personal
          ? {
              ...(bankruptName ? { bankruptName } : {}),
              ...(result.bankruptDateOfBirth
                ? { bankruptDateOfBirth: result.bankruptDateOfBirth }
                : {}),
            }
          : {};
        if (!preAppointment) {
          setDayOneFields({
            courtName: result.courtName,
            courtState: result.courtState,
            courtNumber: result.courtNumber,
            sequestrationNumber: result.sequestrationNumber,
            proceedingMatterNumber: result.proceedingMatterNumber,
            proceedingYear: result.proceedingYear,
            courtOrderDate: result.courtOrderDate,
            windingUpApplicationDate: result.windingUpApplicationDate,
            administrationStartDate: result.administrationStartDate,
            plaintiffName: result.plaintiffName,
            defendantName: result.defendantName,
            bankruptName: result.bankruptName,
            administrationNumber: result.administrationNumber,
            petitioningCreditorCosts: result.petitioningCreditorCosts,
            actOfBankruptcyDate: result.actOfBankruptcyDate,
            ...personalIdentity,
          });
        } else if (personal) {
          setDayOneFields((prev) => ({ ...prev, ...personalIdentity }));
        }
        if (!personal && (result.entityName || result.acn || result.abn)) {
          setAbrPopulated(true);
        }
        // Practitioners / appointees are only added when the extracted name
        // matches an existing org member. External names get dropped so we
        // don't end up with team rows that have no email / user record.
        const orgMembersList = orgMembersRef.current ?? [];

        // Exact (case-insensitive) display-name match — the original
        // behaviour, kept as a fallback for when the LLM suggestion pass is
        // unavailable or finds nothing.
        const applyExactMatch = () => {
          if (!result.practitioners?.length || !orgMembersList.length) return;
          const appointeeSet = new Set(
            (result.appointees ?? []).map((n) => n.trim().toLowerCase()),
          );
          setAddedMembers((prev) => {
            const next = [...prev];
            for (const rawName of result.practitioners ?? []) {
              const trimmed = rawName.trim();
              if (!trimmed) continue;
              const match = orgMembersList.find((m) => {
                const display = getDisplayName(
                  m.user.firstName,
                  m.user.lastName,
                  m.user.email,
                ).toLowerCase();
                return display === trimmed.toLowerCase();
              });
              if (!match) continue;
              const isAppointee = appointeeSet.has(trimmed.toLowerCase());
              // Already in the list (e.g. pre-filled via all staff / a
              // team) — upgrade to appointee if extraction says so, never
              // downgrade a manual tick.
              const existingIdx = next.findIndex((m) => m.id === match._id);
              if (existingIdx >= 0) {
                if (isAppointee && !next[existingIdx].isAppointee) {
                  next[existingIdx] = {
                    ...next[existingIdx],
                    isAppointee: true,
                  };
                }
                continue;
              }
              next.push({
                id: match._id,
                userId: match.user._id,
                name: getDisplayName(
                  match.user.firstName,
                  match.user.lastName,
                  match.user.email,
                ),
                email: match.user.email,
                isAppointee,
                source: "org",
              });
            }
            return next;
          });
        };

        // Second pass — LLM-assisted member matching (handles initials,
        // reversed order, nicknames) plus document variant / tag suggestions,
        // using org context the extraction action doesn't have. Silently
        // pre-fills; the user can change anything manually afterwards.
        const llmMatched = await (async (): Promise<boolean> => {
          const suggestOrgId = orgIdRef.current;
          if (!suggestOrgId) return false;
          try {
            const suggestion = await suggestNoaJobSetup({
              orgId: suggestOrgId,
              practitioners: result.practitioners,
              appointees: result.appointees,
              entityName: result.entityName,
              appointmentType: result.appointmentType,
              registeredAddress: result.registeredAddress,
              rawSummary: result.extras?.rawSummary,
            });

            const matched = suggestion.matchedMembers.length > 0;
            if (matched) {
              setAddedMembers((prev) => {
                const next = [...prev];
                for (const {
                  membershipId,
                  isAppointee,
                } of suggestion.matchedMembers) {
                  const match = orgMembersList.find(
                    (m) => m._id === membershipId,
                  );
                  if (!match) continue;
                  // Already in the list (e.g. pre-filled via all staff /
                  // a team) — upgrade to appointee if the LLM flagged
                  // them, never downgrade a manual tick.
                  const existingIdx = next.findIndex((m) => m.id === match._id);
                  if (existingIdx >= 0) {
                    if (isAppointee && !next[existingIdx].isAppointee) {
                      next[existingIdx] = {
                        ...next[existingIdx],
                        isAppointee: true,
                      };
                    }
                    continue;
                  }
                  next.push({
                    id: match._id,
                    userId: match.user._id,
                    name: getDisplayName(
                      match.user.firstName,
                      match.user.lastName,
                      match.user.email,
                    ),
                    email: match.user.email,
                    isAppointee,
                    source: "org",
                  });
                }
                return next;
              });
            }

            if (Object.keys(suggestion.variantSelections).length > 0) {
              setVariantSelections((prev) => ({
                ...prev,
                ...suggestion.variantSelections,
              }));
            }
            if (Object.keys(suggestion.customTagSelections).length > 0) {
              setCustomTagSelections((prev) => ({
                ...prev,
                ...suggestion.customTagSelections,
              }));
            }
            return matched;
          } catch (err) {
            console.error("[NOA] job-setup suggestion pass failed", err);
            return false;
          }
        })();

        // Fall back to exact-match member pre-fill when the LLM pass didn't
        // match anyone (error, disabled, or no confident matches).
        if (!llmMatched) {
          applyExactMatch();
        }

        setAutofill(flags);
        setNoaPhase("done");
        // Hand off straight to review — no manual click needed.
        setStep("review");
        setActiveSection("all");
      } catch (error) {
        const message =
          error instanceof Error
            ? error.message
            : isPreAppointmentRef.current
              ? "Failed to extract fields from the uploaded document"
              : "Failed to extract fields from Notice of Appointment";
        setNoaPhase("error");
        setNoaError(message);
        toast.error(message);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const clearNoaSelection = () => {
    setNoaFile(null);
    setNoaStorageId(null);
    setNoaPhase("idle");
    setNoaError("");
    if (noaInputRef.current) noaInputRef.current.value = "";
  };

  // MOR-997 — bulk document dump. Each picked file is pushed to storage
  // straight away (same upload-URL mutation as the NOA step); submit then
  // only hands the storage ids to createJob. A failed upload stays in the
  // list with its error so the user can remove it and try again.
  const handleExtraDocsSelected = useCallback(
    (files: File[]) => {
      for (const file of files) {
        if (file.size > 25 * 1024 * 1024) {
          toast.error(`${file.name} is too large. Maximum size is 25 MB.`);
          continue;
        }
        const entryId = `${file.name}-${file.size}-${Date.now()}-${Math.random()
          .toString(36)
          .slice(2)}`;
        const fileType = file.type || "application/octet-stream";
        setExtraDocs((prev) => [
          ...prev,
          {
            id: entryId,
            fileName: file.name,
            fileType,
            fileSize: file.size,
            storageId: null,
            phase: "uploading",
          },
        ]);
        void (async () => {
          try {
            const uploadUrl = await generateNoaUploadUrl({});
            const response = await fetch(uploadUrl, {
              method: "POST",
              headers: { "Content-Type": fileType },
              body: file,
            });
            if (!response.ok) throw new Error("Upload failed");
            const json = (await response.json()) as {
              storageId: Id<"_storage">;
            };
            setExtraDocs((prev) =>
              prev.map((d) =>
                d.id === entryId
                  ? { ...d, storageId: json.storageId, phase: "done" }
                  : d,
              ),
            );
          } catch (error) {
            const message =
              error instanceof Error ? error.message : "Upload failed";
            setExtraDocs((prev) =>
              prev.map((d) =>
                d.id === entryId ? { ...d, phase: "error", error: message } : d,
              ),
            );
          }
        })();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const handleRemoveExtraDoc = (id: string) => {
    setExtraDocs((prev) => prev.filter((d) => d.id !== id));
  };

  const buildPractitionersList = (): string[] => {
    return addedMembers.map((m) => m.name);
  };

  const buildAppointeesList = (): string[] => {
    return addedMembers.filter((m) => m.isAppointee).map((m) => m.name);
  };

  const buildTeamMemberUserIds = (): Id<"users">[] => {
    return addedMembers.flatMap((m) =>
      m.source === "org" && m.userId ? [m.userId as Id<"users">] : [],
    );
  };

  // MOR-867 — job-level role per member (only non-default picks are sent;
  // the backend defaults everyone else to "member").
  const buildTeamMemberRoles = (): Record<Id<"users">, JobRole> => {
    const roles: Record<Id<"users">, JobRole> = {};
    for (const m of addedMembers) {
      if (m.source === "org" && m.userId && m.role && m.role !== "member") {
        roles[m.userId as Id<"users">] = m.role;
      }
    }
    return roles;
  };

  // MOR-748 — workflow role picks, restricted to people still on the job.
  const buildWorkflowRoleAssignments = (): Array<{
    roleId: Id<"workflowRoles">;
    userId: Id<"users">;
  }> => {
    const memberIds = new Set(buildTeamMemberUserIds());
    return Object.entries(workflowRoleSelections)
      .filter(([, userId]) => memberIds.has(userId as Id<"users">))
      .map(([roleId, userId]) => ({
        roleId: roleId as Id<"workflowRoles">,
        userId: userId as Id<"users">,
      }));
  };

  // MOR-748 — the team table shows workflow role as a column on each member,
  // but the assignment is stored role → user (a role has exactly one holder).
  // These three bridge the two shapes.
  const hasWorkflowRoles = !!workflowRoles && workflowRoles.length > 0;

  /** The role this user currently holds, if any. */
  const workflowRoleForUser = (userId: string): Id<"workflowRoles"> | null => {
    const hit = Object.entries(workflowRoleSelections).find(
      ([, uid]) => uid === userId,
    );
    return hit ? (hit[0] as Id<"workflowRoles">) : null;
  };

  /**
   * Give this user a role, or clear theirs. Because one person holds one row
   * in the column and one role has one holder, this drops the user's previous
   * role and takes the target role off whoever held it.
   */
  const handleMemberWorkflowRole = (
    userId: string,
    roleId: Id<"workflowRoles"> | null,
  ) => {
    setWorkflowRoleSelections((prev) => {
      const next: Record<string, string> = {};
      for (const [rid, uid] of Object.entries(prev)) {
        if (uid === userId) continue; // release this user's old role
        if (roleId && rid === roleId) continue; // take the role off its holder
        next[rid] = uid;
      }
      if (roleId) next[roleId] = userId;
      return next;
    });
  };

  /** Roles nobody on the job fills yet, so the gaps can be named. */
  const unfilledRoles = (workflowRoles ?? []).filter(
    (role) => !workflowRoleSelections[role._id],
  );

  /** Avatars live on the org member list, not on the added-member rows. */
  const orgUserById = new Map(
    (orgMembers ?? []).map((m: OrgMemberItem) => [m.user._id, m.user]),
  );

  const handleAddOrgMember = (membershipId: string) => {
    if (addedMembers.some((m) => m.id === membershipId)) return;

    const member = orgMembers?.find(
      (m: OrgMemberItem) => m._id === membershipId,
    );
    if (!member) return;

    const name = getDisplayName(
      member.user.firstName,
      member.user.lastName,
      member.user.email,
    );

    setAddedMembers((prev) => [
      ...prev,
      {
        id: membershipId,
        userId: member.user._id,
        name,
        email: member.user.email,
        isAppointee: false,
        source: "org",
      },
    ]);
    setTeamSearch("");
    setTeamDropdownOpen(false);
  };

  const handleAddExternal = () => {
    const email = teamSearch.trim();
    if (!email) return;
    if (addedMembers.some((m) => m.email === email)) return;

    setAddedMembers((prev) => [
      ...prev,
      {
        id: `ext-${email}`,
        name: email,
        email,
        isAppointee: false,
        source: "external",
      },
    ]);
    setTeamSearch("");
    setTeamDropdownOpen(false);
  };

  const handleRemoveMember = (id: string) => {
    const removed = addedMembers.find((m) => m.id === id);
    setAddedMembers((prev) => prev.filter((m) => m.id !== id));
    // A removed person can't fill a workflow role on the job.
    if (removed?.userId) {
      setWorkflowRoleSelections((prev) =>
        Object.fromEntries(
          Object.entries(prev).filter(
            ([, userId]) => userId !== removed.userId,
          ),
        ),
      );
    }
  };

  // MOR-867 — change a member's job-level role in the added list.
  const handleMemberRoleChange = (id: string, role: JobRole) => {
    setAddedMembers((prev) =>
      prev.map((m) => (m.id === id ? { ...m, role } : m)),
    );
  };

  // MOR-722 — "All staff" / team checkboxes materialise people into
  // `addedMembers` so each person shows as a normal row with the Appointee
  // toggle (and NOA extraction can flag appointees among them). Checkbox
  // state is derived from the list, so it stays accurate when individual
  // rows are removed.
  type OrgTeamItem = NonNullable<typeof orgTeams>[number];

  const allStaffSelected =
    !!orgMembers &&
    orgMembers.length > 0 &&
    (orgMembers as OrgMemberItem[]).every((m) =>
      addedMembers.some((a) => a.id === m._id),
    );

  const handleToggleAllStaff = () => {
    const membersList = (orgMembers ?? []) as OrgMemberItem[];
    if (!membersList.length) return;
    if (allStaffSelected) {
      const orgIds = new Set(membersList.map((m) => m._id));
      setAddedMembers((prev) => prev.filter((m) => !orgIds.has(m.id)));
      return;
    }
    setAddedMembers((prev) => {
      const next = [...prev];
      for (const member of membersList) {
        if (next.some((m) => m.id === member._id)) continue;
        next.push({
          id: member._id,
          userId: member.user._id,
          name: getDisplayName(
            member.user.firstName,
            member.user.lastName,
            member.user.email,
          ),
          email: member.user.email,
          isAppointee: false,
          source: "org",
        });
      }
      return next;
    });
  };

  const isTeamSelected = (team: OrgTeamItem) =>
    team.members.length > 0 &&
    team.members.every((tm) =>
      addedMembers.some((a) => a.userId === tm.userId),
    );

  const handleToggleTeam = (team: OrgTeamItem) => {
    if (isTeamSelected(team)) {
      const teamUserIds = new Set(team.members.map((tm) => tm.userId));
      setAddedMembers((prev) =>
        prev.filter(
          (m) => !(m.userId && teamUserIds.has(m.userId as Id<"users">)),
        ),
      );
      // Drop workflow-role picks pointing at the removed people.
      setWorkflowRoleSelections((prev) =>
        Object.fromEntries(
          Object.entries(prev).filter(
            ([, userId]) => !teamUserIds.has(userId as Id<"users">),
          ),
        ),
      );
      return;
    }
    const membersList = (orgMembers ?? []) as OrgMemberItem[];
    setAddedMembers((prev) => {
      const next = [...prev];
      for (const tm of team.members) {
        if (next.some((m) => m.userId === tm.userId)) continue;
        const orgMember = membersList.find((m) => m.user._id === tm.userId);
        next.push({
          id: orgMember?._id ?? `team-${tm.userId}`,
          userId: tm.userId,
          name: getDisplayName(tm.firstName, tm.lastName, tm.email),
          email: tm.email,
          isAppointee: false,
          source: "org",
          // MOR-867 — the member's team default job role pre-selects here.
          role: tm.defaultJobRole,
        });
      }
      return next;
    });
    // MOR-748 — pre-fill workflow roles from team member defaults. First
    // pick per role wins; existing picks are never overwritten.
    setWorkflowRoleSelections((prev) => {
      const next = { ...prev };
      for (const tm of team.members) {
        if (tm.defaultWorkflowRoleId && !next[tm.defaultWorkflowRoleId]) {
          next[tm.defaultWorkflowRoleId] = tm.userId;
        }
      }
      return next;
    });
  };

  const handleToggleAppointee = (id: string) => {
    setAddedMembers((prev) =>
      prev.map((m) =>
        m.id === id ? { ...m, isAppointee: !m.isAppointee } : m,
      ),
    );
  };

  const handleTeamKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (filteredOrgMembers.length > 0) {
        handleAddOrgMember(filteredOrgMembers[0]._id);
      } else if (teamSearch.includes("@")) {
        handleAddExternal();
      }
    }
  };

  const availableOrgMembers = orgMembers?.filter(
    (m: OrgMemberItem) => !addedMembers.some((added) => added.id === m._id),
  );

  const filteredOrgMembers = (availableOrgMembers ?? []).filter(
    (member: OrgMemberItem) => {
      if (!teamSearch) return true;
      const query = teamSearch.toLowerCase();
      const name = getDisplayName(
        member.user.firstName,
        member.user.lastName,
        member.user.email,
      ).toLowerCase();
      return (
        name.includes(query) || member.user.email.toLowerCase().includes(query)
      );
    },
  );

  const getCurrentDraft = useCallback(
    (): CreateJobDraft => ({
      kind: jobKind ?? undefined,
      caseType,
      jobName,
      jobCode: jobCodeEdited ? jobCode : undefined,
      entityName,
      acn,
      abn,
      isTrustee,
      trustAbn,
      trustName,
      registeredAddress,
      registeredOfficeStreet,
      registeredOfficeSuburb,
      registeredOfficeState,
      registeredOfficePostcode,
      principalPlaceStreet,
      principalPlaceSuburb,
      principalPlaceState,
      principalPlacePostcode,
      principalPlaceSameAsRegistered,
      companyNameVariants,
      anzsicCode,
      anzsicReasoning,
      appointmentType,
      appointmentDate,
      notes,
      addedMembers,
      workflowRoleAssignments: workflowRoleSelections,
      referrerFirmContactId: referrerId ? String(referrerId) : undefined,
      dayOneFields,
      windingUpApplicationStatus: windingUpStatus,
    }),
    [
      jobKind,
      caseType,
      jobName,
      jobCode,
      jobCodeEdited,
      entityName,
      acn,
      abn,
      isTrustee,
      trustAbn,
      trustName,
      registeredAddress,
      registeredOfficeStreet,
      registeredOfficeSuburb,
      registeredOfficeState,
      registeredOfficePostcode,
      principalPlaceStreet,
      principalPlaceSuburb,
      principalPlaceState,
      principalPlacePostcode,
      principalPlaceSameAsRegistered,
      companyNameVariants,
      anzsicCode,
      anzsicReasoning,
      appointmentType,
      appointmentDate,
      notes,
      addedMembers,
      workflowRoleSelections,
      referrerId,
      dayOneFields,
      windingUpStatus,
    ],
  );

  useEffect(() => {
    if (!open || !draftRestored) return;
    const draft = getCurrentDraft();
    if (!hasDraftContent(draft)) return;
    const timeout = window.setTimeout(() => saveDraft(draft), 300);
    return () => window.clearTimeout(timeout);
  }, [open, draftRestored, getCurrentDraft]);

  /** Clear every input and go back to the kind chooser. Leaves the saved draft alone. */
  const resetFormState = () => {
    setStep("kind");
    setJobKind(null);
    setCaseType("corporate");
    setJobName("");
    setJobCode("");
    setJobCodeEdited(false);
    setEntityName("");
    setAcn("");
    setAbn("");
    setIsTrustee(false);
    setTrustAbn("");
    setTrustName("");
    setCompanyHasNoAbnHint(false);
    setRegisteredAddress("");
    setRegisteredOfficeStreet("");
    setRegisteredOfficeSuburb("");
    setRegisteredOfficeState("");
    setRegisteredOfficePostcode("");
    setPrincipalPlaceStreet("");
    setPrincipalPlaceSuburb("");
    setPrincipalPlaceState("");
    setPrincipalPlacePostcode("");
    setPrincipalPlaceSameAsRegistered(true);
    setCompanyNameVariants([]);
    setAnzsicCode("");
    setAnzsicSearch("");
    setAnzsicDropdownOpen(false);
    setAnzsicReasoning("");
    setAppointmentType("");
    setAppointmentDate("");
    setNotes("");
    setAddedMembers([]);
    setWorkflowRoleSelections({});
    setTeamSearch("");
    setTeamDropdownOpen(false);
    setNameSearch("");
    setNameResults([]);
    setNameDropdownOpen(false);
    setNameSearchEmpty(false);
    setAbrPopulated(false);
    setCustomTagSelections({});
    setDayOneFields({});
    setWindingUpStatus(undefined);
    setAutofill({});
    setActiveSection("all");
    setReferrerId(null);
    setAddReferrerOpen(false);
    setExtraDocs([]);
    if (extraDocsInputRef.current) extraDocsInputRef.current.value = "";
    clearNoaSelection();
    abnPopulatedRef.current = false;
    acnPopulatedRef.current = false;
    trustAbnPopulatedRef.current = false;
    abnLookupFiredRef.current = "";
    acnLookupFiredRef.current = "";
    trustAbnLookupFiredRef.current = "";
  };

  /** A job was created (or the draft is otherwise finished with): forget it. */
  const resetForm = () => {
    clearDraft();
    resetFormState();
  };

  /**
   * MOR-1858 (QA-05) — closing the dialog keeps the draft. The autosave is
   * debounced, so flush the latest values first; reopening restores them on
   * the All tab. Only a successful Create clears the draft.
   */
  const handleDismiss = () => {
    const draft = getCurrentDraft();
    if (hasDraftContent(draft)) saveDraft(draft);
    else clearDraft();
    resetFormState();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!orgId) {
      toast.error("No organisation selected");
      return;
    }

    // MOR-998 — validation failures land the user on the tab that holds
    // the offending field, so the toast isn't pointing at hidden content.
    const failOn = (tab: typeof activeSection, message: string): boolean => {
      setActiveSection(tab);
      toast.error(message);
      return true;
    };

    if (!jobName.trim() && failOn("details", "Job name is required")) return;

    if (
      !entityName.trim() &&
      failOn(
        "details",
        isPersonal
          ? "Bankrupt estate name is required"
          : "Company name is required",
      )
    ) {
      return;
    }

    if (isPersonal) {
      // MOR-470 — Personal Insolvency (Bankrupt Estate) intake. An individual
      // has no ACN/ABN/ABR record and no registered office, so those corporate
      // requirements are skipped. The bankrupt's name is the required
      // identifier; court details are optional here (typically supplied via the
      // court order).
      if (
        !dayOneFields.bankruptName?.trim() &&
        failOn("details", "Bankrupt name is required")
      ) {
        return;
      }
    } else {
      if (!acn.trim() && failOn("details", "ACN is required")) return;

      // Company ABN is optional: every company has an ACN, but not every
      // company registers an ABN (e.g. a trustee-only company, or a company
      // that never registered for GST / an ABN). Trust ABN is still required
      // when the trust section is enabled.
      if (
        isTrustee &&
        !trustAbn.trim() &&
        failOn(
          "details",
          "Trust ABN is required when the trust section is enabled.",
        )
      ) {
        return;
      }

      if (
        !registeredOfficeStreet.trim() &&
        failOn("details", "Registered office street address is required")
      ) {
        return;
      }

      if (
        !registeredOfficeSuburb.trim() &&
        failOn("details", "Registered office suburb is required")
      ) {
        return;
      }

      if (
        !registeredOfficeState.trim() &&
        failOn("details", "Registered office state is required")
      ) {
        return;
      }

      if (
        !registeredOfficePostcode.trim() &&
        failOn("details", "Registered office postcode is required")
      ) {
        return;
      }
    }

    if (
      !isPreAppointment &&
      !appointmentType &&
      failOn("details", "Appointment type is required")
    ) {
      return;
    }

    if (
      !isPreAppointment &&
      !appointmentDate &&
      failOn("details", "Appointment date is required")
    ) {
      return;
    }

    // MOR-997 — don't create the job while document uploads are mid-flight
    // (the storage ids don't exist yet) or failed (silent data loss).
    if (
      extraDocs.some((d) => d.phase === "uploading") &&
      failOn("documents", "Documents are still uploading — one moment.")
    ) {
      return;
    }
    if (
      extraDocs.some((d) => d.phase === "error") &&
      failOn(
        "documents",
        "Some documents failed to upload — remove them or try again.",
      )
    ) {
      return;
    }

    const practitioners = buildPractitionersList();
    const appointees = buildAppointeesList();
    const teamMemberUserIds = buildTeamMemberUserIds();
    const teamMemberRoles = buildTeamMemberRoles();
    const workflowRoleAssignments = buildWorkflowRoleAssignments();

    try {
      const hasCustomTags = Object.values(customTagSelections).some(
        (v) => v.length > 0,
      );
      const trimmedNotes = notes.trim();
      const normalisedCompanyNameVariants =
        normaliseCompanyNameVariants(companyNameVariants);

      // Compute the principal place fields: use registered office values if "same" is checked
      const resolvedPrincipalStreet = principalPlaceSameAsRegistered
        ? registeredOfficeStreet.trim()
        : principalPlaceStreet.trim();
      const resolvedPrincipalSuburb = principalPlaceSameAsRegistered
        ? registeredOfficeSuburb.trim()
        : principalPlaceSuburb.trim();
      const resolvedPrincipalState = principalPlaceSameAsRegistered
        ? registeredOfficeState.trim()
        : principalPlaceState.trim();
      const resolvedPrincipalPostcode = principalPlaceSameAsRegistered
        ? registeredOfficePostcode.trim()
        : principalPlacePostcode.trim();

      // Build the combined registeredAddress string for backward compat
      const fullRegisteredAddress = [
        registeredOfficeStreet.trim(),
        registeredOfficeSuburb.trim(),
        registeredOfficeState.trim(),
        registeredOfficePostcode.trim(),
      ]
        .filter(Boolean)
        .join(", ");

      const resolvedKind: JobCreationKind = jobKind ?? "post_appointment";

      const jobId = await createJob({
        orgId,
        kind: resolvedKind,
        jobName: jobName.trim(),
        jobCode: jobCode.trim() || undefined,
        entityName: entityName.trim(),
        acn: acn.trim(),
        abn: abn.trim() || undefined,
        trustAbn: isTrustee && trustAbn.trim() ? trustAbn.trim() : undefined,
        trustName: isTrustee && trustName.trim() ? trustName.trim() : undefined,
        trusteeCapacity: trusteeCapacity,
        registeredAddress: fullRegisteredAddress,
        registeredOfficeStreet: registeredOfficeStreet.trim(),
        registeredOfficeSuburb: registeredOfficeSuburb.trim(),
        registeredOfficeState: registeredOfficeState.trim(),
        registeredOfficePostcode: registeredOfficePostcode.trim(),
        principalPlaceStreet: resolvedPrincipalStreet || undefined,
        principalPlaceSuburb: resolvedPrincipalSuburb || undefined,
        principalPlaceState: resolvedPrincipalState || undefined,
        principalPlacePostcode: resolvedPrincipalPostcode || undefined,
        companyNameVariants: normalisedCompanyNameVariants.length
          ? normalisedCompanyNameVariants
          : undefined,
        anzsicCode: anzsicCode || undefined,
        appointmentType: appointmentType || undefined,
        // MOR-881 — the appointment date input is hidden on the
        // pre-appointment form, so never submit one. Belt-and-braces against
        // a value left in state by an earlier post-appointment extraction
        // (the user can go Back and switch kinds mid-wizard).
        appointmentDate: isPreAppointment
          ? undefined
          : appointmentDate || undefined,
        practitioners,
        appointees,
        teamMemberUserIds,
        teamMemberRoles:
          Object.keys(teamMemberRoles).length > 0 ? teamMemberRoles : undefined,
        workflowRoleAssignments:
          workflowRoleAssignments.length > 0
            ? workflowRoleAssignments
            : undefined,
        customTagSelections: hasCustomTags ? customTagSelections : undefined,
        variantSelections:
          Object.keys(variantSelections).length > 0
            ? variantSelections
            : undefined,
        notes: trimmedNotes ? trimmedNotes : undefined,
        // MOR-881 — both kinds can attach a document now. Pre-appointment
        // jobs usually attach an ASIC notice / search rather than a true
        // instrument; it lands in the same `notice_of_appointment` slot and
        // is replaced by the real NOA at conversion.
        noticeOfAppointmentStorageId:
          noaStorageId && noaFile ? noaStorageId : undefined,
        noticeOfAppointmentFileName: noaFile?.name,
        noticeOfAppointmentFileType: noaFile?.type,
        noticeOfAppointmentFileSize: noaFile?.size,
        // MOR-996 — referrer captured on the creation form.
        referrerFirmContactId: referrerId ?? undefined,
        // MOR-997 — the bulk document dump; uploads were completed as the
        // files were picked (guarded above), so every entry has a storage id.
        additionalDocuments: (() => {
          const docs = extraDocs
            .filter((d) => d.phase === "done" && d.storageId)
            .map((d) => ({
              storageId: d.storageId as Id<"_storage">,
              fileName: d.fileName,
              fileType: d.fileType,
              fileSize: d.fileSize,
            }));
          return docs.length > 0 ? docs : undefined;
        })(),
        // MOR-429 day-one fields. Spread last so any explicit undefined in
        // dayOneFields doesn't accidentally clobber an earlier value (none
        // overlap with createJob's other args today, but the spread order
        // future-proofs us). MOR-881 — the court fields are hidden on the
        // pre-appointment form and captured at conversion instead, so never
        // sent for that kind. MOR-1847 — the individual's identity (name and
        // date of birth) IS on the pre-appointment personal form and is what
        // the conflict check circulates, so it always goes through.
        ...(isPreAppointment
          ? {
              bankruptName: isPersonal ? dayOneFields.bankruptName : undefined,
              bankruptDateOfBirth: isPersonal
                ? dayOneFields.bankruptDateOfBirth
                : undefined,
            }
          : dayOneFields),
        // MOR-901 — the prior winding-up application question is only shown
        // post-appointment, so this stays undefined for pre-appointment jobs.
        windingUpApplicationStatus: isPreAppointment
          ? undefined
          : windingUpStatus,
      });

      toast.success(`Job created: ${jobName.trim()}`);
      setOpen(false);
      resetForm();
      navigate({
        to: "/dashboard/jobs/$jobId/gather-information",
        params: { jobId },
      });
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to create job";
      toast.error(message);
    }
  };

  const isLookingUp = abr.isAbnLoading || abr.isAcnLoading;

  // Every way into the review form lands on the All tab (MOR-1858); only a
  // failed Create jumps to the tab holding the offending field.
  const handleSkipUpload = () => {
    clearNoaSelection();
    setStep("review");
    setActiveSection("all");
  };

  const handleContinueAfterUpload = () => {
    if (noaPhase === "extracting" || noaPhase === "uploading") return;
    setStep("review");
    setActiveSection("all");
  };

  /**
   * Form 1: kind chooser. Both kinds route on through the case-type
   * chooser and then the document upload step.
   */
  const handleSelectKind = (selectedKind: JobCreationKind) => {
    setJobKind(selectedKind);
  };

  /**
   * Advance from the kind chooser (step 1) to the case-type chooser
   * (step 2). The case-type step then routes onward to upload/review.
   */
  const handleContinueFromKind = () => {
    if (!jobKind) return;
    setStep("caseType");
  };

  /**
   * Advance from the case-type chooser (step 2) into the document upload.
   * MOR-881 — both kinds go through the upload now. Post-appointment
   * uploads the instrument; pre-appointment uploads whatever company
   * document they do have (usually an ASIC notice or ASIC search), which
   * runs through the same extraction to pre-fill the form. Skip is still
   * available on that step for anyone with nothing to upload.
   */
  const handleContinueFromCaseType = () => {
    setStep("upload");
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) handleDismiss();
        setOpen(nextOpen);
      }}
    >
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="flex max-h-[90vh] min-h-0 flex-col gap-0 overflow-visible p-0 sm:max-w-2xl">
        <DialogHeader className="px-6 py-5">
          <DialogTitle>
            {step === "kind"
              ? "Create New Job"
              : step === "caseType"
                ? "Is this a Corporate or Personal Insolvency?"
                : isPreAppointment
                  ? "Create Pre-Appointment Job"
                  : "Create Post-Appointment Job"}
          </DialogTitle>
          {step === "kind" && (
            <p className="text-sm text-muted-foreground">
              Select the current phase of the job. You can convert a
              pre-appointment job into an appointment at any time.
            </p>
          )}
          {step === "caseType" && (
            <p className="text-sm text-muted-foreground">
              Corporate appointments use company details while personal
              appointments use bankruptee details.
            </p>
          )}
        </DialogHeader>

        {step === "kind" ? (
          <div className="flex-1 overflow-y-auto px-6 pb-2 pt-2">
            <KindChooserStep
              selectedKind={jobKind}
              onSelectKind={handleSelectKind}
            />
          </div>
        ) : step === "caseType" ? (
          <div className="flex-1 overflow-y-auto px-6 pb-2 pt-2">
            <CaseTypeChooserStep
              selectedCaseType={caseType}
              onSelectCaseType={setCaseType}
            />
          </div>
        ) : step === "upload" ? (
          <div className="flex-1 overflow-y-auto px-6 py-6">
            <NoaUploadStep
              isPreAppointment={isPreAppointment}
              file={noaFile}
              phase={noaPhase}
              error={noaError}
              fileInputRef={noaInputRef}
              onFileSelected={handleNoaFileSelected}
              onClear={clearNoaSelection}
            />
          </div>
        ) : (
          /* MOR-998 — the review form is real tab panels (Radix Tabs). The
           tab list lives OUTSIDE the <form> so a tab click can never submit
           it; the form wraps every panel so the footer's submit button
           (form="createJobForm") still works. Tabs whose sections have
           nothing to show (no variant/tag groups) are simply not rendered,
           which was the old scroll-spy's "clicking does nothing" bug. */
          <Tabs
            value={activeSection}
            onValueChange={(v) => setActiveSection(v as typeof activeSection)}
            className="flex min-h-0 flex-1 flex-col"
          >
            <TabsList
              variant="underline"
              className="w-full shrink-0 flex-nowrap justify-start gap-3 overflow-x-auto px-6 [scrollbar-width:none]"
            >
              {REVIEW_TABS.filter((tab) => {
                if (tab.id === "variants") {
                  return !!variantGroups && variantGroups.length > 0;
                }
                if (tab.id === "tags") {
                  return !!tagGroups && tagGroups.length > 0;
                }
                return true;
              }).map((tab) => (
                <TabsTrigger
                  key={tab.id}
                  variant="underline"
                  value={tab.id}
                  className="shrink-0"
                >
                  <tab.icon className="h-3.5 w-3.5" aria-hidden />
                  {tab.label}
                  {tab.id === "documents" && extraDocs.length > 0 && (
                    <span className="rounded-full bg-muted px-1.5 text-[10px] tabular-nums">
                      {extraDocs.length}
                    </span>
                  )}
                </TabsTrigger>
              ))}
            </TabsList>
            {/* noValidate: every panel stays mounted (so the All tab can stack
            them), and a native `required` check on a hidden input would
            block submit silently. handleSubmit validates and jumps to the
            right tab instead. */}
            <form
              id="createJobForm"
              onSubmit={handleSubmit}
              noValidate
              className={cn(
                "flex min-h-0 flex-1 flex-col overflow-y-auto px-6 py-6",
                activeSection === "all" && "gap-8",
              )}
            >
              <ReviewPanel tab="details" active={activeSection}>
                {/* Job Name / Title + Company Name */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex flex-col gap-2">
                    <div className="flex items-center gap-2">
                      <Label htmlFor="jobName">Job Name / Title *</Label>
                    </div>
                    <Input
                      id="jobName"
                      placeholder="Add short title for internal use"
                      value={jobName}
                      onChange={(e) => {
                        setJobName(e.target.value);
                        if (autofill.jobName)
                          setAutofill((prev) => ({ ...prev, jobName: false }));
                      }}
                      required
                    />
                  </div>

                  <div className="flex flex-col gap-2">
                    <div className="flex items-center gap-2">
                      <Label htmlFor="entityName">
                        {isPersonal
                          ? "Bankrupt Estate Name *"
                          : "Company Name *"}
                      </Label>
                    </div>
                    {isPersonal ? (
                      <Input
                        id="entityName"
                        placeholder="e.g. Bankrupt Estate of Michael Jackson"
                        value={entityName}
                        onChange={(e) => setEntityName(e.target.value)}
                        required
                      />
                    ) : (
                      <div className="relative">
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                          <Input
                            ref={nameInputRef}
                            id="entityName"
                            placeholder="Search by company name, ABN, or ACN"
                            className="pl-9"
                            value={nameSearch || entityName}
                            onChange={(e) => {
                              const val = e.target.value;
                              setNameSearch(val);
                              // Only update entityName when typing text, not raw digits
                              const stripped = val.replace(/\s/g, "");
                              if (!/^\d+$/.test(stripped)) {
                                setEntityName(val);
                              }
                              setAbrPopulated(false);
                              setNameSearchEmpty(false);
                              if (!val) {
                                setNameResults([]);
                                setNameDropdownOpen(false);
                              }
                            }}
                            onFocus={() => {
                              if (nameResults.length > 0 && !abrPopulated) {
                                setNameDropdownOpen(true);
                              }
                            }}
                            onBlur={() => {
                              setTimeout(() => setNameDropdownOpen(false), 200);
                            }}
                            required
                          />
                          {(abr.isNameLoading ||
                            ((abr.isAbnLoading || abr.isAcnLoading) &&
                              nameSearch &&
                              /^\d[\d\s]*$/.test(nameSearch))) && (
                            <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground" />
                          )}
                          {abrPopulated &&
                            !abr.isNameLoading &&
                            !(abr.isAbnLoading || abr.isAcnLoading) && (
                              <Check className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-emerald-500" />
                            )}
                        </div>

                        {/* Digit hint — shown when user is typing partial ABN/ACN */}
                        {(() => {
                          if (!nameSearch || nameSearchEmpty) return null;
                          const stripped = nameSearch.replace(/\s/g, "");
                          if (!/^\d+$/.test(stripped)) return null;
                          if (stripped.length === 9 || stripped.length === 11)
                            return null;
                          return (
                            <p className="mt-1 text-xs text-muted-foreground">
                              Enter 9 digits for ACN or 11 digits for ABN lookup
                            </p>
                          );
                        })()}

                        {/* No results message */}
                        {nameSearchEmpty &&
                          !abr.isNameLoading &&
                          !abr.isAbnLoading &&
                          !abr.isAcnLoading && (
                            <p className="mt-1 text-xs text-muted-foreground">
                              No matching companies found — try a different
                              name, ABN, or ACN
                            </p>
                          )}

                        {/* Search results dropdown */}
                        {nameDropdownOpen && nameResults.length > 0 && (
                          <div className="absolute z-50 mt-1 w-full rounded-md border bg-popover p-1 shadow-md">
                            {nameResults.map((result) => (
                              <button
                                key={result.abn}
                                type="button"
                                className="flex w-full items-start gap-3 rounded-sm px-3 py-2 text-left hover:bg-muted"
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => handleSelectNameResult(result)}
                              >
                                <Building2 className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                                <div className="flex flex-col gap-0.5 overflow-hidden">
                                  <span className="truncate text-sm font-medium">
                                    {titleCaseCompanyName(result.name)}
                                  </span>
                                  <span className="text-xs text-muted-foreground">
                                    ABN {formatABN(result.abn)}
                                    {result.state &&
                                      ` · ${result.state} ${result.postcode}`}
                                    {result.nameType === "trading" &&
                                      " · Trading name"}
                                  </span>
                                </div>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Job code — sent to InfoTrack as the client reference so search
              costs reconcile to this job. Pre-filled with the firm's next
              suggested code; editable, and blank falls back to the auto-mint. */}
                <div className="flex flex-col gap-2">
                  <Label htmlFor="jobCode">Job Code</Label>
                  <Input
                    id="jobCode"
                    placeholder={suggestedJobCode || "J-0001"}
                    value={jobCode}
                    onChange={(e) => {
                      setJobCode(e.target.value);
                      setJobCodeEdited(true);
                    }}
                  />
                  <p className="text-xs text-muted-foreground">
                    Sent to InfoTrack as the client reference for cost
                    reconciliation.
                  </p>
                </div>

                {/* MOR-470 — Personal Insolvency identifiers. Shown only for personal
              cases; replaces the ABR-driven company lookup, ACN/ABN and trust
              fields. */}
                {isPersonal && (
                  <>
                    <div className="flex flex-col gap-2">
                      <Label htmlFor="bankruptName">Bankrupt Name *</Label>
                      <Input
                        id="bankruptName"
                        placeholder="e.g. Michael Jackson"
                        value={dayOneFields.bankruptName ?? ""}
                        onChange={(e) => {
                          const value = e.target.value;
                          setDayOneField("bankruptName", value);
                          // Convenience: seed the estate name from the bankrupt's
                          // name while the estate field is still empty. Once the user
                          // types an estate name we leave it alone.
                          if (!entityName.trim() && value.trim()) {
                            setEntityName(`Bankrupt Estate of ${value.trim()}`);
                          }
                        }}
                        required
                      />
                      <p className="text-xs text-muted-foreground">
                        The bankrupt estate name above is suggested from this —
                        edit it directly if needed.
                      </p>
                    </div>

                    {/* MOR-1847 — identifies the individual on the conflict
                  check (Name / DOB / Address). Optional: left blank, the
                  check says "Not recorded". */}
                    <div className="flex flex-col gap-2">
                      <Label>Date of Birth</Label>
                      <DatePicker
                        value={dayOneFields.bankruptDateOfBirth ?? ""}
                        onChange={(value) =>
                          setDayOneField("bankruptDateOfBirth", value)
                        }
                        placeholder="DD/MM/YYYY"
                      />
                    </div>

                    {/* Court file reference — substitutes for the ACN line on
                  personal insolvency documents. Sourced from the court order. */}
                    <div className="flex flex-col gap-3">
                      <Label>Court File Reference</Label>
                      <Input
                        placeholder="Court name (e.g. Federal Circuit and Family Court of Australia)"
                        value={dayOneFields.courtName ?? ""}
                        onChange={(e) =>
                          setDayOneField("courtName", e.target.value)
                        }
                      />
                      <div className="grid grid-cols-3 gap-3">
                        <Input
                          placeholder="State"
                          value={dayOneFields.courtState ?? ""}
                          onChange={(e) =>
                            setDayOneField("courtState", e.target.value)
                          }
                        />
                        <Input
                          placeholder="File No. (e.g. SYG1316/2025)"
                          value={dayOneFields.sequestrationNumber ?? ""}
                          onChange={(e) =>
                            setDayOneField(
                              "sequestrationNumber",
                              e.target.value,
                            )
                          }
                        />
                        <DatePicker
                          value={dayOneFields.courtOrderDate ?? ""}
                          onChange={(value) =>
                            setDayOneField("courtOrderDate", value)
                          }
                          placeholder="Court order date"
                        />
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Taken from the court order. Used in place of the ACN
                        line on generated documents.
                      </p>
                    </div>
                  </>
                )}

                {/* Additional company name lines */}
                {!isPersonal && (
                  <div className="flex flex-col gap-3 rounded-lg border bg-muted/20 p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <Label>Additional Names</Label>
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handleAddCompanyNameVariant}
                      >
                        <Plus className="mr-2 h-4 w-4" />
                        Add name
                      </Button>
                    </div>

                    {companyNameVariants.length > 0 ? (
                      <div className="flex flex-col gap-3">
                        {companyNameVariants.map((variant, index) => (
                          <div
                            key={`${variant.source}-${variant.type}-${index}`}
                            className="grid gap-3 rounded-md border bg-background p-3 md:grid-cols-[minmax(0,1fr)_180px_auto]"
                          >
                            <div className="flex flex-col gap-1.5">
                              <div className="flex items-center gap-2">
                                <div className="relative flex-1">
                                  <Input
                                    aria-label="Additional company name"
                                    value={variant.name}
                                    onChange={(e) =>
                                      handleUpdateCompanyNameVariant(index, {
                                        name: e.target.value,
                                      })
                                    }
                                    placeholder="Enter additional company name"
                                  />
                                  {variant.name.trim() && (
                                    <Check className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-emerald-500" />
                                  )}
                                </div>
                                {variant.source === "abr" && (
                                  <span className="shrink-0 rounded-full bg-primary/10 px-2 py-0.5 text-xs text-primary">
                                    ABR
                                  </span>
                                )}
                              </div>
                              <label className="flex items-center gap-2 text-xs text-muted-foreground">
                                <Checkbox
                                  checked={variant.includeInDocuments}
                                  onCheckedChange={(checked) =>
                                    handleUpdateCompanyNameVariant(index, {
                                      includeInDocuments: checked === true,
                                    })
                                  }
                                />
                                Include in generated documents
                              </label>
                            </div>

                            <Select
                              value={variant.type}
                              onValueChange={(value) =>
                                handleUpdateCompanyNameVariant(index, {
                                  type: value as CompanyNameVariantType,
                                })
                              }
                            >
                              <SelectTrigger aria-label="Name type">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {COMPANY_NAME_VARIANT_TYPES.map((type) => (
                                  <SelectItem key={type} value={type}>
                                    {COMPANY_NAME_VARIANT_TYPE_LABELS[type]}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>

                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="justify-self-end"
                              onClick={() =>
                                handleRemoveCompanyNameVariant(index)
                              }
                              aria-label="Remove additional company name"
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-muted-foreground">
                        No additional names identified in ABN lookup. Select
                        &ldquo;Add name&rdquo; to add trading names, former
                        trading names and trust details to the Company name in
                        precedents.
                      </p>
                    )}
                  </div>
                )}

                {/* ACN, ABN & trust — corporate-only (MOR-470). Individuals have no
              ACN/ABN/ABR record. */}
                {!isPersonal && (
                  <>
                    {/* ACN & ABN — with auto-lookup on blur */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="flex flex-col gap-2">
                        <div className="flex items-center gap-2">
                          <Label htmlFor="acn">ACN *</Label>
                        </div>
                        <div className="relative">
                          <Input
                            id="acn"
                            placeholder="000 000 000"
                            value={acn}
                            onChange={(e) => {
                              acnPopulatedRef.current = false;
                              acnLookupFiredRef.current = "";
                              setAcn(formatACN(e.target.value));
                              if (autofill.acn)
                                setAutofill((prev) => ({
                                  ...prev,
                                  acn: false,
                                }));
                            }}
                            required
                          />
                          {abr.isAcnLoading && (
                            <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground" />
                          )}
                        </div>
                      </div>
                      <div className="flex flex-col gap-2">
                        <div className="flex items-center gap-2">
                          <Label htmlFor="abn">ABN</Label>
                        </div>
                        <div className="relative">
                          <Input
                            id="abn"
                            placeholder="00 000 000 000"
                            value={abn}
                            onChange={(e) => {
                              abnPopulatedRef.current = false;
                              abnLookupFiredRef.current = "";
                              setAbn(formatABN(e.target.value));
                              if (autofill.abn)
                                setAutofill((prev) => ({
                                  ...prev,
                                  abn: false,
                                }));
                            }}
                          />
                          {abr.isAbnLoading && (
                            <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground" />
                          )}
                        </div>
                      </div>
                    </div>

                    {isLookingUp && (
                      <p className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Loader2 className="h-3 w-3 animate-spin" />
                        Looking up company details...
                      </p>
                    )}

                    {/* MOR-340 — Trust section. Shown when the user toggles on the
              trust capacity (or auto-toggled by the no-ABN hint below). */}
                    <div className="flex flex-col gap-3 rounded-md border border-dashed border-muted-foreground/30 p-3">
                      <label className="flex items-start gap-2 text-sm">
                        <Checkbox
                          checked={isTrustee}
                          onCheckedChange={(checked) => {
                            const next = checked === true;
                            setIsTrustee(next);
                            if (!next) {
                              setTrustAbn("");
                              setTrustName("");
                              trustAbnPopulatedRef.current = false;
                              trustAbnLookupFiredRef.current = "";
                            }
                            setCompanyHasNoAbnHint(false);
                          }}
                        />
                        <span className="flex flex-col gap-0.5">
                          <span className="font-medium">
                            This company is trustee of a trust
                          </span>
                          <span className="text-xs text-muted-foreground">
                            Capture the trust&apos;s ABN separately. If the
                            company only acts as trustee, leave the company ABN
                            above blank.
                          </span>
                        </span>
                      </label>

                      {!isTrustee && companyHasNoAbnHint && (
                        <div className="flex items-start gap-2 rounded-md bg-amber-50 p-2 text-xs text-amber-900 dark:bg-amber-900/20 dark:text-amber-200">
                          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                          <span>
                            No ABN found for this company in the ABR. It may
                            operate solely as trustee of a trust — tick the box
                            above to add trust details.
                          </span>
                        </div>
                      )}

                      {isTrustee && (
                        <div className="grid grid-cols-2 gap-4">
                          <div className="flex flex-col gap-2">
                            <Label htmlFor="trustAbn">Trust ABN *</Label>
                            <div className="relative">
                              <Input
                                id="trustAbn"
                                placeholder="00 000 000 000"
                                value={trustAbn}
                                onChange={(e) => {
                                  trustAbnPopulatedRef.current = false;
                                  trustAbnLookupFiredRef.current = "";
                                  setTrustAbn(formatABN(e.target.value));
                                }}
                              />
                              {abr.isAbnLoading && (
                                <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground" />
                              )}
                            </div>
                          </div>
                          <div className="flex flex-col gap-2">
                            <Label htmlFor="trustName">Trust name</Label>
                            <Input
                              id="trustName"
                              placeholder="Auto-filled from ABR"
                              value={trustName}
                              onChange={(e) => setTrustName(e.target.value)}
                            />
                          </div>
                        </div>
                      )}

                      {showDualCapacityWarning && (
                        <div className="flex items-start gap-2 rounded-md bg-amber-50 p-2 text-xs text-amber-900 dark:bg-amber-900/20 dark:text-amber-200">
                          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                          <span>
                            <strong>Dual capacity:</strong> the company has its
                            own ABN and is also trustee of a trust. It may have
                            traded in both capacities — investigation should
                            consider both entities.
                          </span>
                        </div>
                      )}
                    </div>
                  </>
                )}

                {/* Registered / bankrupt's address. Required for corporate; optional
              for personal insolvency (MOR-470). */}
                <div className="flex flex-col gap-3">
                  <Label>
                    {isPersonal
                      ? "Bankrupt's Address"
                      : "Registered Office Address *"}
                  </Label>
                  <div className="grid grid-cols-1 gap-3">
                    <Input
                      placeholder="Street address"
                      value={registeredOfficeStreet}
                      onChange={(e) =>
                        setRegisteredOfficeStreet(e.target.value)
                      }
                      required={!isPersonal}
                    />
                    <div className="grid grid-cols-3 gap-3">
                      <Input
                        placeholder="Suburb"
                        value={registeredOfficeSuburb}
                        onChange={(e) =>
                          setRegisteredOfficeSuburb(e.target.value)
                        }
                        required={!isPersonal}
                      />
                      <Input
                        placeholder="State"
                        value={registeredOfficeState}
                        onChange={(e) =>
                          setRegisteredOfficeState(e.target.value)
                        }
                        required={!isPersonal}
                      />
                      <Input
                        placeholder="Postcode"
                        value={registeredOfficePostcode}
                        onChange={(e) =>
                          setRegisteredOfficePostcode(e.target.value)
                        }
                        required={!isPersonal}
                      />
                    </div>
                  </div>
                </div>

                {/* Principal Place of Business + ANZSIC — corporate-only (MOR-470) */}
                {!isPersonal && (
                  <>
                    <div className="flex flex-col gap-3">
                      <div className="flex items-center justify-between">
                        <Label>Principal Place of Business</Label>
                        <label className="flex items-center gap-2 text-xs text-muted-foreground">
                          <Checkbox
                            checked={principalPlaceSameAsRegistered}
                            onCheckedChange={(checked) =>
                              setPrincipalPlaceSameAsRegistered(
                                checked === true,
                              )
                            }
                          />
                          Same as registered office
                        </label>
                      </div>
                      {!principalPlaceSameAsRegistered && (
                        <div className="grid grid-cols-1 gap-3">
                          <Input
                            placeholder="Street address"
                            value={principalPlaceStreet}
                            onChange={(e) =>
                              setPrincipalPlaceStreet(e.target.value)
                            }
                          />
                          <div className="grid grid-cols-3 gap-3">
                            <Input
                              placeholder="Suburb"
                              value={principalPlaceSuburb}
                              onChange={(e) =>
                                setPrincipalPlaceSuburb(e.target.value)
                              }
                            />
                            <Input
                              placeholder="State"
                              value={principalPlaceState}
                              onChange={(e) =>
                                setPrincipalPlaceState(e.target.value)
                              }
                            />
                            <Input
                              placeholder="Postcode"
                              value={principalPlacePostcode}
                              onChange={(e) =>
                                setPrincipalPlacePostcode(e.target.value)
                              }
                            />
                          </div>
                        </div>
                      )}
                    </div>

                    {/* ANZSIC Code */}
                    <div className="flex flex-col gap-2">
                      <Label>ANZSIC Code</Label>
                      <div className="relative">
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                          <Input
                            placeholder="Search by code or industry name"
                            className="pl-9"
                            value={
                              anzsicSearch ||
                              (anzsicCode
                                ? (() => {
                                    const match = ANZSIC_CODES.find(
                                      (c) => c.code === anzsicCode,
                                    );
                                    return match
                                      ? `${match.code} — ${match.description}`
                                      : anzsicCode;
                                  })()
                                : "")
                            }
                            onChange={(e) => {
                              setAnzsicSearch(e.target.value);
                              setAnzsicCode("");
                              setAnzsicReasoning("");
                              setAnzsicDropdownOpen(true);
                            }}
                            onFocus={() => {
                              if (!anzsicCode) setAnzsicDropdownOpen(true);
                            }}
                            onBlur={() => {
                              setTimeout(
                                () => setAnzsicDropdownOpen(false),
                                200,
                              );
                            }}
                          />
                          {anzsicCode && (
                            <button
                              type="button"
                              onClick={() => {
                                setAnzsicCode("");
                                setAnzsicSearch("");
                                setAnzsicReasoning("");
                              }}
                              className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full p-0.5 text-muted-foreground hover:text-foreground"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          )}
                        </div>

                        {anzsicDropdownOpen && (
                          <div className="absolute z-50 mt-1 max-h-60 w-full overflow-y-auto rounded-md border bg-popover p-1 shadow-md">
                            {(() => {
                              const query = anzsicSearch.toLowerCase();
                              const filtered = query
                                ? ANZSIC_CODES.filter(
                                    (c) =>
                                      c.code.includes(query) ||
                                      c.description
                                        .toLowerCase()
                                        .includes(query) ||
                                      c.divisionName
                                        .toLowerCase()
                                        .includes(query),
                                  )
                                : ANZSIC_CODES;
                              if (filtered.length === 0) {
                                return (
                                  <p className="px-3 py-2 text-sm text-muted-foreground">
                                    No matching codes
                                  </p>
                                );
                              }
                              return filtered.slice(0, 50).map((code) => (
                                <button
                                  key={code.code}
                                  type="button"
                                  className="flex w-full items-start gap-3 rounded-sm px-3 py-2 text-left hover:bg-muted"
                                  onMouseDown={(e) => e.preventDefault()}
                                  onClick={() => {
                                    setAnzsicCode(code.code);
                                    setAnzsicSearch("");
                                    setAnzsicDropdownOpen(false);
                                    setAnzsicReasoning("");
                                  }}
                                >
                                  <span className="shrink-0 font-mono text-sm text-muted-foreground">
                                    {code.code}
                                  </span>
                                  <div className="flex flex-col gap-0.5 overflow-hidden">
                                    <span className="truncate text-sm">
                                      {code.description}
                                    </span>
                                    <span className="text-xs text-muted-foreground">
                                      {code.division} — {code.divisionName}
                                    </span>
                                  </div>
                                </button>
                              ));
                            })()}
                          </div>
                        )}
                      </div>
                      {anzsicReasoning && (
                        <div className="flex flex-col gap-1.5 rounded-md border border-dashed border-muted-foreground/30 bg-muted/40 px-3 py-2">
                          <div className="flex items-center gap-1.5">
                            <Sparkles className="h-3 w-3 text-muted-foreground" />
                            <p className="text-xs font-medium text-muted-foreground">
                              AI Suggestion
                            </p>
                          </div>
                          <p className="text-xs text-muted-foreground">
                            {anzsicReasoning}
                          </p>
                          <p className="text-[11px] text-muted-foreground/60">
                            AI generated. Check all results
                          </p>
                        </div>
                      )}
                      {abrPopulated && !anzsicCode && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="w-full gap-1.5 text-xs"
                          disabled={abr.isAnzsicGuessing}
                          onClick={handleSuggestAnzsic}
                        >
                          {abr.isAnzsicGuessing ? (
                            <>
                              <Loader2 className="h-3 w-3 animate-spin" />
                              Researching...
                            </>
                          ) : (
                            <>
                              <Sparkles className="h-3 w-3" />
                              Suggest with AI
                            </>
                          )}
                        </Button>
                      )}
                    </div>
                  </>
                )}

                {/* Appointment Type + Date.
                 *
                 * For pre-appointment jobs the date is unknown (the
                 * instrument doesn't exist yet) and the type may also be
                 * tentative. The type stays editable as an optional hint;
                 * the date is hidden and captured later via
                 * `convertPreAppointmentJob`.
                 */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex flex-col gap-2">
                    <div className="flex items-center gap-2">
                      <Label>Appointment Type{!isPreAppointment && " *"}</Label>
                      {isPreAppointment && (
                        <span className="text-xs text-muted-foreground">
                          (optional)
                        </span>
                      )}
                    </div>
                    <Select
                      value={appointmentType || undefined}
                      onValueChange={(v) => {
                        setAppointmentType(v as AppointmentType);
                        if (autofill.appointmentType)
                          setAutofill((prev) => ({
                            ...prev,
                            appointmentType: false,
                          }));
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue
                          placeholder={
                            isPreAppointment
                              ? "Select intended type (optional)"
                              : "Select appointment type"
                          }
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {[
                          ...(isPersonal
                            ? PERSONAL_APPOINTMENT_TYPES
                            : CORPORATE_APPOINTMENT_TYPES),
                        ].map((value) => (
                          <SelectItem key={value} value={value}>
                            {APPOINTMENT_TYPE_LABELS[value]}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {!isPreAppointment && (
                    <div className="flex flex-col gap-2">
                      <div className="flex items-center gap-2">
                        <Label>Appointment Date *</Label>
                      </div>
                      <DatePicker
                        value={appointmentDate}
                        onChange={(value) => {
                          setAppointmentDate(value);
                          if (autofill.appointmentDate)
                            setAutofill((prev) => ({
                              ...prev,
                              appointmentDate: false,
                            }));
                        }}
                        placeholder="Select appointment date"
                      />
                    </div>
                  )}
                </div>

                {/* MOR-901 — prior winding-up application. Only a voluntary
              administration can be preceded by one, and it sets the
              relation-back day, so it is captured at intake rather than
              discovered later when the review windows are already wrong. The
              same section appears in Job Settings → Appointment, which carries
              the heading, the legislative reasoning and the override input —
              intake only needs the answer. */}
                {!isPreAppointment && (
                  <RelationBackFieldsSection
                    appointmentType={appointmentType}
                    context={{
                      appointmentDate,
                      administrationStartDate:
                        dayOneFields.administrationStartDate ?? "",
                    }}
                    applicationDate={
                      dayOneFields.windingUpApplicationDate ?? ""
                    }
                    onApplicationDateChange={(next) =>
                      setDayOneField("windingUpApplicationDate", next)
                    }
                    status={windingUpStatus}
                    onStatusChange={(next) => {
                      setWindingUpStatus(next);
                      // Answering "no" retracts the filing date — keeping it would
                      // leave a date on the job for an application that never existed.
                      if (next === undefined || next === "none") {
                        setDayOneField("windingUpApplicationDate", "");
                      }
                    }}
                    showGuidance={false}
                    disabled={isPending}
                  />
                )}

                {isPreAppointment && (
                  <p className="-mt-2 flex items-start gap-1.5 rounded-md border border-dashed bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
                    <Info className="mt-0.5 h-3 w-3 shrink-0" />
                    The appointment date is captured when this job is converted
                    after the instrument of appointment is received. Any
                    document you uploaded is kept on the job.
                  </p>
                )}
              </ReviewPanel>

              {/* MOR-996 — referral captured at creation instead of tagging the
              job in the pipeline afterwards. */}
              <ReviewPanel tab="referral" active={activeSection}>
                <div className="flex flex-col gap-3">
                  <div className="flex flex-col gap-1">
                    <Label className="flex items-center gap-1.5">
                      <Handshake className="size-3.5" />
                      Referrer
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      Who referred this job to the firm? Recording it here feeds
                      the referrer&apos;s conversion stats and the
                      pipeline&apos;s referrer view.
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <SearchableSelect
                      value={referrerId ? String(referrerId) : "__none__"}
                      onValueChange={(value) =>
                        setReferrerId(
                          value === "__none__"
                            ? null
                            : (value as Id<"firmContacts">),
                        )
                      }
                      options={[
                        { value: "__none__", label: "No referrer" },
                        // MOR-995 — nested people render as "Person — Firm".
                        ...(referrers ?? []).map((r) => ({
                          value: String(r._id),
                          label: r.parentDisplayName
                            ? `${r.displayName} — ${r.parentDisplayName}`
                            : r.displayName,
                        })),
                      ]}
                      placeholder="Tag a referrer…"
                      searchPlaceholder="Search referrers…"
                      emptyMessage={
                        canCreateReferrer
                          ? "No referrer contacts yet — add one with the New button."
                          : "No referrer contacts yet."
                      }
                      className="flex-1"
                      side="bottom"
                      avoidCollisions={false}
                      contentClassName="z-[100]"
                    />
                    {canCreateReferrer && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="shrink-0 gap-1"
                        onClick={() => setAddReferrerOpen(true)}
                        title="Add a new referrer"
                      >
                        <Plus className="size-3.5" />
                        New
                      </Button>
                    )}
                  </div>
                </div>
              </ReviewPanel>

              {/* Add Team */}
              <ReviewPanel tab="team" active={activeSection}>
                <div className="flex flex-col gap-3">
                  <Label>Add Team</Label>
                  <p className="text-xs text-muted-foreground">
                    Add team members to this job — individually, by team, or all
                    staff.
                  </p>

                  {/* MOR-722 — quick-select: ticking adds each person to the
                member list below (with the Appointee toggle) */}
                  {((orgMembers?.length ?? 0) > 0 ||
                    (orgTeams?.length ?? 0) > 0) && (
                    <div className="flex flex-col gap-2 rounded-md border p-3">
                      <label className="flex cursor-pointer items-center justify-between gap-2">
                        <span className="text-sm font-medium">All staff</span>
                        <Checkbox
                          checked={allStaffSelected}
                          onCheckedChange={handleToggleAllStaff}
                        />
                      </label>

                      {orgTeams && orgTeams.length > 0 && (
                        <div className="flex flex-col gap-1 border-t pt-2">
                          {orgTeams.map((team) => (
                            <label
                              key={team._id}
                              className="flex cursor-pointer items-center justify-between gap-2 rounded-sm px-1 py-1 hover:bg-muted"
                            >
                              <span className="text-sm">
                                {team.name}
                                <span className="ml-2 text-xs text-muted-foreground">
                                  {team.members.length} member
                                  {team.members.length !== 1 ? "s" : ""}
                                </span>
                              </span>
                              <Checkbox
                                checked={isTeamSelected(team)}
                                disabled={team.members.length === 0}
                                onCheckedChange={() => handleToggleTeam(team)}
                              />
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Search / add team member */}
                  <div className="relative">
                    <Input
                      placeholder="Search members or enter email"
                      value={teamSearch}
                      onChange={(e) => {
                        setTeamSearch(e.target.value);
                        setTeamDropdownOpen(true);
                      }}
                      onFocus={() => setTeamDropdownOpen(true)}
                      onBlur={() => {
                        setTimeout(() => setTeamDropdownOpen(false), 150);
                      }}
                      onKeyDown={handleTeamKeyDown}
                    />
                    {teamDropdownOpen &&
                      (filteredOrgMembers.length > 0 ||
                        (teamSearch.includes("@") &&
                          !addedMembers.some(
                            (m) => m.email === teamSearch.trim(),
                          ))) && (
                        <div className="absolute z-50 mt-1 w-full rounded-md border bg-popover p-1 shadow-md">
                          {filteredOrgMembers.map((member: OrgMemberItem) => {
                            const name = getDisplayName(
                              member.user.firstName,
                              member.user.lastName,
                              member.user.email,
                            );
                            return (
                              <button
                                key={member._id}
                                type="button"
                                className="flex w-full flex-col rounded-sm px-3 py-2 text-left hover:bg-muted"
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => handleAddOrgMember(member._id)}
                              >
                                <span className="text-sm">{name}</span>
                                <span className="text-xs text-muted-foreground">
                                  {member.user.email}
                                </span>
                              </button>
                            );
                          })}
                          {teamSearch.includes("@") &&
                            !addedMembers.some(
                              (m) => m.email === teamSearch.trim(),
                            ) && (
                              <button
                                type="button"
                                className="flex w-full items-center gap-2 rounded-sm px-3 py-2 text-left text-sm hover:bg-muted"
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={handleAddExternal}
                              >
                                Invite &ldquo;{teamSearch.trim()}&rdquo; to this
                                job
                              </button>
                            )}
                        </div>
                      )}
                  </div>

                  {/* One table, same shape as the org settings team modal: who is on
                the job, whether they are an appointee, which workflow role
                they fill, and their job-level role. A workflow role has a
                single holder, so assigning one here takes it from whoever
                held it before. */}
                  {addedMembers.length > 0 && (
                    <div className="flex flex-col gap-2 rounded-md border p-3">
                      <div className="flex items-baseline justify-between gap-2">
                        <span className="text-xs font-medium text-muted-foreground">
                          Added Members
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {(() => {
                            const orgCount = addedMembers.filter(
                              (m) => m.source === "org",
                            ).length;
                            const extCount = addedMembers.filter(
                              (m) => m.source === "external",
                            ).length;
                            const parts: string[] = [];
                            if (orgCount > 0) parts.push(`${orgCount} added`);
                            if (extCount > 0) parts.push(`${extCount} invited`);
                            return parts.join(", ");
                          })()}
                        </span>
                      </div>
                      <div className="overflow-hidden rounded-md border">
                        <Table>
                          <TableHeader>
                            <TableRow className="hover:bg-transparent">
                              <TableHead className="h-9 px-3 text-xs">
                                Member
                              </TableHead>
                              <TableHead className="h-9 w-[74px] px-3 text-center text-xs">
                                Appointee
                              </TableHead>
                              {hasWorkflowRoles && (
                                <TableHead className="h-9 w-[170px] px-3 text-xs">
                                  Workflow role
                                </TableHead>
                              )}
                              <TableHead className="h-9 w-[130px] px-3 text-xs">
                                Job role
                              </TableHead>
                              <TableHead className="h-9 w-[44px] px-3" />
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {addedMembers.map((member) => {
                              const orgUser = member.userId
                                ? orgUserById.get(member.userId)
                                : undefined;
                              return (
                                <TableRow key={member.id}>
                                  <TableCell className="px-3 py-2">
                                    <div className="flex min-w-0 items-center gap-2">
                                      <UserAvatar
                                        avatarUrl={orgUser?.avatarUrl}
                                        firstName={orgUser?.firstName}
                                        lastName={orgUser?.lastName}
                                        email={member.email}
                                        className="h-7 w-7 shrink-0"
                                      />
                                      <div className="flex min-w-0 flex-col">
                                        <span className="truncate text-sm font-medium">
                                          {member.name}
                                        </span>
                                        <span className="truncate text-xs text-muted-foreground">
                                          {member.email}
                                        </span>
                                      </div>
                                    </div>
                                  </TableCell>
                                  <TableCell className="px-3 py-2 text-center">
                                    <Checkbox
                                      checked={member.isAppointee}
                                      onCheckedChange={() =>
                                        handleToggleAppointee(member.id)
                                      }
                                      aria-label={`Mark ${member.name} as an appointee`}
                                      className="h-3.5 w-3.5"
                                    />
                                  </TableCell>
                                  {hasWorkflowRoles && (
                                    <TableCell className="px-3 py-2">
                                      {/* Only org members can hold a workflow role:
                                    external invitees have no user id to
                                    resolve role-assigned tasks to. */}
                                      {member.source === "org" &&
                                      member.userId ? (
                                        <WorkflowRoleSelect
                                          roles={workflowRoles ?? []}
                                          value={workflowRoleForUser(
                                            member.userId,
                                          )}
                                          onChange={(roleId) =>
                                            handleMemberWorkflowRole(
                                              member.userId as string,
                                              roleId,
                                            )
                                          }
                                        />
                                      ) : (
                                        <span className="text-xs text-muted-foreground">
                                          Not applicable
                                        </span>
                                      )}
                                    </TableCell>
                                  )}
                                  <TableCell className="px-3 py-2">
                                    {/* MOR-867 — set the member's job role up front so
                                  permissions are right from day one. External
                                  invitees get theirs when they accept. */}
                                    {member.source === "org" ? (
                                      <JobRoleSelect
                                        value={member.role ?? "member"}
                                        onChange={(role) =>
                                          handleMemberRoleChange(
                                            member.id,
                                            (role ?? "member") as JobRole,
                                          )
                                        }
                                      />
                                    ) : (
                                      <span className="text-xs text-muted-foreground">
                                        Invited
                                      </span>
                                    )}
                                  </TableCell>
                                  <TableCell className="px-3 py-2">
                                    <button
                                      type="button"
                                      onClick={() =>
                                        handleRemoveMember(member.id)
                                      }
                                      aria-label={`Remove ${member.name}`}
                                      className="rounded-full p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                                    >
                                      <X className="h-3 w-3" />
                                    </button>
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </div>

                      {/* A role with no holder is invisible in a per-member column,
                    so name the gaps rather than let them pass silently. */}
                      {hasWorkflowRoles && unfilledRoles.length > 0 && (
                        <p className="text-xs text-muted-foreground">
                          No one assigned yet for{" "}
                          {unfilledRoles.map((r) => r.name).join(", ")}.
                          Workflow tasks for those roles stay unassigned.
                        </p>
                      )}
                    </div>
                  )}

                  {addedMembers.length === 0 && hasWorkflowRoles && (
                    <p className="text-xs text-muted-foreground">
                      Add team members above to assign workflow roles.
                    </p>
                  )}
                </div>
              </ReviewPanel>

              {/* MOR-997 — bulk document dump. Firms arrive with a pile of
              documents; attach them all here so the job starts complete
              instead of uploading one by one afterwards. */}
              <ReviewPanel
                tab="documents"
                active={activeSection}
                className="gap-4"
              >
                <div className="flex flex-col gap-1">
                  <Label>Attach documents</Label>
                  <p className="text-xs text-muted-foreground">
                    Add any documents you already have for this job —
                    they&apos;re filed in the job&apos;s document library and
                    classified automatically once the job is created.
                  </p>
                </div>
                <div
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    const files = Array.from(e.dataTransfer.files ?? []);
                    if (files.length > 0) handleExtraDocsSelected(files);
                  }}
                  className="flex flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed bg-muted/20 p-8 text-center"
                >
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
                    <Upload className="h-5 w-5" />
                  </div>
                  <div className="flex flex-col gap-1">
                    <p className="text-sm font-medium">
                      Drag and drop files here
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Any file type, up to 25 MB each
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => extraDocsInputRef.current?.click()}
                  >
                    Choose files
                  </Button>
                  <input
                    ref={extraDocsInputRef}
                    type="file"
                    multiple
                    className="hidden"
                    onChange={(e) => {
                      const files = Array.from(e.target.files ?? []);
                      if (files.length > 0) handleExtraDocsSelected(files);
                      e.target.value = "";
                    }}
                  />
                </div>
                {extraDocs.length > 0 && (
                  <ul className="flex flex-col gap-1.5">
                    {extraDocs.map((doc) => (
                      <li
                        key={doc.id}
                        className="flex items-center gap-2 rounded-md border px-3 py-2"
                      >
                        <FileText className="h-4 w-4 shrink-0 text-primary" />
                        <div className="flex min-w-0 flex-1 flex-col">
                          <span className="truncate text-sm font-medium">
                            {doc.fileName}
                          </span>
                          {doc.phase === "uploading" && (
                            <span className="flex items-center gap-1 text-xs text-muted-foreground">
                              <Loader2 className="h-3 w-3 animate-spin" />
                              Uploading…
                            </span>
                          )}
                          {doc.phase === "done" && (
                            <span className="flex items-center gap-1 text-xs text-emerald-600">
                              <Check className="h-3 w-3" />
                              Ready
                            </span>
                          )}
                          {doc.phase === "error" && (
                            <span className="text-xs text-destructive">
                              {doc.error || "Upload failed"}
                            </span>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => handleRemoveExtraDoc(doc.id)}
                          aria-label={`Remove ${doc.fileName}`}
                          className="rounded-full p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </ReviewPanel>

              {/* Variant selections — pick a single option per variant group.
              These drive sentence-level variant blocks inside templates so
              every generated doc renders the right wording. */}
              {variantGroups && variantGroups.length > 0 && (
                <ReviewPanel tab="variants" active={activeSection}>
                  <Collapsible defaultOpen>
                    <CollapsibleTrigger className="flex w-full items-center justify-between">
                      <div className="flex flex-col items-start gap-1">
                        <Label className="pointer-events-none">
                          Document Variants
                        </Label>
                        <p className="text-xs text-muted-foreground">
                          Choose the wording variant for each option — applied
                          to every generated document.
                        </p>
                      </div>
                      <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground transition-transform data-[state=open]:rotate-180" />
                    </CollapsibleTrigger>
                    <CollapsibleContent className="mt-3 space-y-3">
                      {variantGroups.map((group) => (
                        <div key={group._id} className="flex flex-col gap-1.5">
                          <span className="text-xs font-medium text-foreground/70">
                            {group.name}
                          </span>
                          <div className="flex flex-wrap gap-1.5">
                            {group.options.map(
                              (opt: { value: string; label: string }) => {
                                const selected =
                                  variantSelections[group.slug] === opt.value;
                                return (
                                  <button
                                    key={opt.value}
                                    type="button"
                                    className={cn(
                                      "flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs transition",
                                      selected
                                        ? "border-primary/40 bg-primary/10 text-primary"
                                        : "border-border text-muted-foreground hover:border-border/80 hover:text-foreground",
                                    )}
                                    onClick={() => {
                                      setVariantSelections((prev) => ({
                                        ...prev,
                                        [group.slug]: opt.value,
                                      }));
                                    }}
                                  >
                                    <div
                                      className={cn(
                                        "flex size-3.5 shrink-0 items-center justify-center rounded-full border transition",
                                        selected
                                          ? "border-primary bg-primary text-primary-foreground"
                                          : "border-border",
                                      )}
                                    >
                                      {selected && (
                                        <div className="size-1.5 rounded-full bg-current" />
                                      )}
                                    </div>
                                    {opt.label}
                                  </button>
                                );
                              },
                            )}
                          </div>
                        </div>
                      ))}
                    </CollapsibleContent>
                  </Collapsible>
                </ReviewPanel>
              )}

              {/* Custom Tag Groups */}
              {tagGroups && tagGroups.length > 0 && (
                <ReviewPanel tab="tags" active={activeSection}>
                  <Collapsible defaultOpen>
                    <CollapsibleTrigger className="flex w-full items-center justify-between">
                      <div className="flex flex-col items-start gap-1">
                        <Label className="pointer-events-none">
                          Document Tags
                        </Label>
                        <p className="text-xs text-muted-foreground">
                          Select options to auto-suggest matching templates when
                          generating documents.
                        </p>
                      </div>
                      <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground transition-transform data-[state=open]:rotate-180" />
                    </CollapsibleTrigger>
                    <CollapsibleContent className="mt-3 space-y-3">
                      {tagGroups.map((group) => (
                        <div key={group._id} className="flex flex-col gap-1.5">
                          <span className="text-xs font-medium text-foreground/70">
                            {group.name}
                          </span>
                          <div className="flex flex-wrap gap-1.5">
                            {group.options.map(
                              (opt: { value: string; label: string }) => {
                                const selected =
                                  customTagSelections[group.slug]?.includes(
                                    opt.value,
                                  ) ?? false;
                                return (
                                  <button
                                    key={opt.value}
                                    type="button"
                                    className={cn(
                                      "flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs transition",
                                      selected
                                        ? "border-primary/40 bg-primary/10 text-primary"
                                        : "border-border text-muted-foreground hover:border-border/80 hover:text-foreground",
                                    )}
                                    onClick={() => {
                                      setCustomTagSelections((prev) => {
                                        const current = prev[group.slug] ?? [];
                                        const next = selected
                                          ? current.filter(
                                              (v: string) => v !== opt.value,
                                            )
                                          : [...current, opt.value];
                                        return { ...prev, [group.slug]: next };
                                      });
                                    }}
                                  >
                                    <div
                                      className={cn(
                                        "flex size-3.5 shrink-0 items-center justify-center rounded-sm border transition",
                                        selected
                                          ? "border-primary bg-primary text-primary-foreground"
                                          : "border-border",
                                      )}
                                    >
                                      {selected && (
                                        <Check className="size-2.5" />
                                      )}
                                    </div>
                                    {opt.label}
                                  </button>
                                );
                              },
                            )}
                          </div>
                        </div>
                      ))}
                    </CollapsibleContent>
                  </Collapsible>
                </ReviewPanel>
              )}

              {/* Notes — populated from the uploaded document's extraction.
               * MOR-881: shown for both kinds now that pre-appointment jobs
               * can upload an ASIC notice / search and get a summary back.
               */}
              <ReviewPanel tab="notes" active={activeSection}>
                <div className="flex flex-col gap-2">
                  <div className="flex items-center gap-2">
                    <Label htmlFor="jobNotes">Notes</Label>
                  </div>
                  <p className="flex items-start gap-1.5 text-xs text-muted-foreground">
                    <Info className="mt-0.5 h-3 w-3 shrink-0" />
                    {isPreAppointment
                      ? "Additional context extracted from the document you uploaded. Notes are editable and are saved in Job Settings."
                      : "Additional context extracted from the Instrument of Appointment. Notes are editable and are saved in Job Settings."}
                  </p>
                  <div className="relative">
                    <div
                      className={cn(
                        "overflow-hidden transition-[max-height] duration-200",
                        !notesExpanded && "max-h-[120px]",
                      )}
                    >
                      <Textarea
                        id="jobNotes"
                        value={notes}
                        onChange={(e) => {
                          setNotes(e.target.value);
                          if (autofill.notes) {
                            setAutofill((prev) => ({ ...prev, notes: false }));
                          }
                        }}
                        placeholder="Add any additional context for this job"
                        className={cn(
                          "min-h-[100px] resize-none",
                          notesExpanded && "min-h-[200px]",
                        )}
                      />
                    </div>
                    {!notesExpanded && notes.length > 200 && (
                      <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-background to-transparent" />
                    )}
                  </div>
                  {notes.length > 200 && (
                    <button
                      type="button"
                      onClick={() => setNotesExpanded(!notesExpanded)}
                      className="mt-1 self-start rounded-md border border-primary/30 px-3 py-1 text-xs font-medium text-primary transition-colors hover:border-primary/50 hover:bg-primary/5"
                    >
                      {notesExpanded ? "Show less" : "Show more"}
                    </button>
                  )}
                </div>
              </ReviewPanel>
            </form>
          </Tabs>
        )}

        {/* Every footer button carries a step-specific key (MOR-1858). Without
            them React reuses the upload step's Skip button as the review
            step's submit button: the click that moves to review re-renders
            before the browser runs the button's default action, which then
            submits the freshly mounted form — firing validation and jumping
            to the Details tab before the user has typed anything. */}
        <DialogFooter className="relative z-10 shrink-0 bg-background px-6 py-3">
          {step === "kind" ? (
            <Button
              key="kind-continue"
              type="button"
              onClick={handleContinueFromKind}
              disabled={!jobKind}
              className="gap-2"
            >
              Continue
              <ArrowRight className="h-4 w-4" />
            </Button>
          ) : step === "caseType" ? (
            <>
              <Button
                key="case-type-back"
                type="button"
                variant="ghost"
                onClick={() => setStep("kind")}
                className="gap-1"
              >
                <ArrowLeft className="h-4 w-4" />
                Back
              </Button>
              <Button
                key="case-type-continue"
                type="button"
                onClick={handleContinueFromCaseType}
                className="gap-2"
              >
                Continue
                <ArrowRight className="h-4 w-4" />
              </Button>
            </>
          ) : step === "upload" ? (
            <>
              <Button
                key="upload-back"
                type="button"
                variant="ghost"
                onClick={() => setStep("caseType")}
                className="gap-1"
              >
                <ArrowLeft className="h-4 w-4" />
                Back
              </Button>
              <Button
                key="upload-skip"
                type="button"
                variant="outline"
                onClick={handleSkipUpload}
                disabled={noaPhase === "uploading" || noaPhase === "extracting"}
              >
                Skip
              </Button>
              <Button
                key="upload-continue"
                type="button"
                onClick={handleContinueAfterUpload}
                disabled={
                  noaPhase === "uploading" ||
                  noaPhase === "extracting" ||
                  noaPhase === "idle"
                }
                className="gap-2"
              >
                {noaPhase === "uploading" || noaPhase === "extracting" ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {noaPhase === "uploading" ? "Uploading…" : "Extracting…"}
                  </>
                ) : (
                  <>
                    Continue
                    <ArrowRight className="h-4 w-4" />
                  </>
                )}
              </Button>
            </>
          ) : (
            <>
              {/* Clears the persisted draft and every field, returning to the
                  kind chooser. The draft otherwise survives close/reopen
                  (MOR-1858), so this is the only way to abandon a stale draft
                  — e.g. one pre-filled from the wrong document — and start a
                  genuinely clean job. */}
              <Button
                key="review-reset"
                type="button"
                variant="ghost"
                onClick={resetForm}
                disabled={isPending}
                className="mr-auto gap-1 text-muted-foreground"
              >
                <RotateCcw className="h-4 w-4" />
                Start fresh
              </Button>
              <Button
                key="review-back"
                type="button"
                variant="ghost"
                onClick={() => setStep("upload")}
                className="gap-1"
              >
                <ArrowLeft className="h-4 w-4" />
                Back
              </Button>
              <Button
                key="review-create"
                type="submit"
                form="createJobForm"
                disabled={isPending}
              >
                {isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creating...
                  </>
                ) : isPreAppointment ? (
                  "Create Pre-Appointment Job"
                ) : (
                  "Create Job"
                )}
              </Button>
            </>
          )}
        </DialogFooter>

        {/* MOR-996 — create a referrer contact without leaving the wizard;
            the new contact is tagged on the Referral tab immediately. */}
        {orgId && (
          <AddReferrerDialog
            orgId={orgId}
            open={addReferrerOpen}
            onOpenChange={setAddReferrerOpen}
            onCreated={(firmContactId) => setReferrerId(firmContactId)}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Review-step panel
// ---------------------------------------------------------------------------

interface ReviewPanelProps {
  tab: Exclude<ReviewTab, "all">;
  active: ReviewTab;
  className?: string;
  children: ReactNode;
}

/**
 * One section of the review form. Always mounted (`forceMount`) so the
 * "All" tab can stack every section on one page; hidden unless it is the
 * active tab or All is selected. In All mode each section gets an
 * icon + heading so the page reads as a form with labelled parts.
 */
function ReviewPanel({ tab, active, className, children }: ReviewPanelProps) {
  const visible = active === "all" || active === tab;
  const meta = REVIEW_TABS.find((t) => t.id === tab);
  return (
    <TabsContent
      value={tab}
      forceMount
      hidden={!visible}
      className={cn("mt-0", !visible && "hidden")}
    >
      <div className={cn("flex flex-col gap-6", className)}>
        {active === "all" && meta && (
          <div className="flex items-center gap-2 border-b pb-2">
            <meta.icon className="h-4 w-4 text-muted-foreground" aria-hidden />
            <span className="text-sm font-semibold">{meta.heading}</span>
          </div>
        )}
        {children}
      </div>
    </TabsContent>
  );
}

// ---------------------------------------------------------------------------
// Wizard sub-components
// ---------------------------------------------------------------------------

/**
 * Step 1 — pre vs post-appointment chooser. Two large radio-style cards
 * so the user can see at a glance how each path differs.
 */
interface KindChooserStepProps {
  selectedKind: JobCreationKind | null;
  onSelectKind: (kind: JobCreationKind) => void;
}

function KindChooserStep({ selectedKind, onSelectKind }: KindChooserStepProps) {
  return (
    <div className="flex flex-col gap-3">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <KindCard
          kind="pre_appointment"
          selected={selectedKind === "pre_appointment"}
          icon={<ClipboardList className="h-5 w-5" />}
          title="Pre-Appointment"
          description="Select this option if appointment date is not finalised."
          bullets={[
            "No appointment date or instrument required",
            "Convert later to a full appointment job",
            "Run ASIC and PPSR searches now",
          ]}
          onSelect={() => onSelectKind("pre_appointment")}
        />
        <KindCard
          kind="post_appointment"
          selected={selectedKind === "post_appointment"}
          icon={<Briefcase className="h-5 w-5" />}
          title="Post-Appointment"
          description="Select this option if appointment has commenced."
          bullets={[
            "Upload the instrument of appointment",
            "Capture appointment date and notes",
            "Run the full post-appointment search tree",
          ]}
          onSelect={() => onSelectKind("post_appointment")}
        />
      </div>
    </div>
  );
}

/**
 * Step 2 — corporate vs personal insolvency chooser. Drives which
 * appointment types and intake fields the review form shows.
 */
interface CaseTypeChooserStepProps {
  selectedCaseType: CaseType;
  onSelectCaseType: (caseType: CaseType) => void;
}

function CaseTypeChooserStep({
  selectedCaseType,
  onSelectCaseType,
}: CaseTypeChooserStepProps) {
  return (
    <div className="flex flex-col gap-3">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <CaseTypeCard
          value="corporate"
          selected={selectedCaseType === "corporate"}
          icon={<Building2 className="h-5 w-5" />}
          title="Corporate"
          description="Company in liquidation, administration, receivership, etc."
          onSelect={() => onSelectCaseType("corporate")}
        />
        <CaseTypeCard
          value="personal"
          selected={selectedCaseType === "personal"}
          icon={<User className="h-5 w-5" />}
          title="Personal Insolvency"
          description="Bankrupt estate appointed via a court order or other instrument."
          onSelect={() => onSelectCaseType("personal")}
        />
      </div>
    </div>
  );
}

interface CaseTypeCardProps {
  value: CaseType;
  selected: boolean;
  icon: ReactNode;
  title: string;
  description: string;
  onSelect: () => void;
}

function CaseTypeCard({
  value,
  selected,
  icon,
  title,
  description,
  onSelect,
}: CaseTypeCardProps) {
  return (
    <button
      type="button"
      onClick={onSelect}
      data-case-type={value}
      className={cn(
        "flex items-start gap-3 rounded-lg border bg-background p-4 text-left transition-colors",
        "hover:border-emerald-500/60 hover:bg-emerald-50/30 dark:hover:bg-emerald-950/20",
        selected &&
          "border-emerald-500 bg-emerald-50/50 dark:bg-emerald-950/30",
      )}
    >
      <div
        className={cn(
          "flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground",
          selected &&
            "bg-emerald-100 text-emerald-600 dark:bg-emerald-900/40 dark:text-emerald-400",
        )}
      >
        {icon}
      </div>
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold">{title}</span>
          {selected && (
            <Check className="h-4 w-4 text-emerald-500" aria-hidden />
          )}
        </div>
        <p className="text-xs text-muted-foreground">{description}</p>
      </div>
    </button>
  );
}

interface KindCardProps {
  kind: JobCreationKind;
  selected: boolean;
  icon: ReactNode;
  title: string;
  description: string;
  bullets: string[];
  onSelect: () => void;
}

function KindCard({
  kind,
  selected,
  icon,
  title,
  description,
  bullets,
  onSelect,
}: KindCardProps) {
  return (
    <button
      type="button"
      onClick={onSelect}
      data-kind={kind}
      className={cn(
        "flex flex-col gap-3 rounded-lg border bg-background p-4 text-left transition-colors",
        "hover:border-emerald-500/60 hover:bg-emerald-50/30 dark:hover:bg-emerald-950/20",
        selected &&
          "border-emerald-500 bg-emerald-50/50 dark:bg-emerald-950/30",
      )}
    >
      <div className="flex items-center gap-2">
        <div
          className={cn(
            "flex h-8 w-8 items-center justify-center rounded-md bg-muted text-muted-foreground",
            selected &&
              "bg-emerald-100 text-emerald-600 dark:bg-emerald-900/40 dark:text-emerald-400",
          )}
        >
          {icon}
        </div>
        <div className="flex flex-col">
          <span className="text-sm font-semibold">{title}</span>
        </div>
        {selected && (
          <Check className="ml-auto h-4 w-4 text-emerald-500" aria-hidden />
        )}
      </div>
      <p className="text-xs text-muted-foreground">{description}</p>
      <ul className="flex flex-col gap-1 text-xs text-muted-foreground">
        {bullets.map((bullet) => (
          <li key={bullet} className="flex items-start gap-1.5">
            <span
              className="mt-1 h-1 w-1 shrink-0 rounded-full bg-current"
              aria-hidden
            />
            <span>{bullet}</span>
          </li>
        ))}
      </ul>
    </button>
  );
}

interface NoaUploadStepProps {
  /**
   * MOR-881 — pre-appointment jobs run the same upload + AI extraction, but
   * the document is usually an ASIC notice or ASIC company search rather
   * than an instrument of appointment, so the copy differs.
   */
  isPreAppointment: boolean;
  file: File | null;
  phase: "idle" | "uploading" | "extracting" | "done" | "error";
  error: string;
  fileInputRef: React.MutableRefObject<HTMLInputElement | null>;
  onFileSelected: (file: File) => void;
  onClear: () => void;
}

function NoaUploadStep({
  isPreAppointment,
  file,
  phase,
  error,
  fileInputRef,
  onFileSelected,
  onClear,
}: NoaUploadStepProps) {
  const instrumentLabel = isPreAppointment
    ? "document"
    : "Instrument of Appointment";
  const promptLabel = isPreAppointment
    ? "Add an ASIC search, ASIC notice or other company document to automate job creation"
    : "Add Instrument of Appointment to automate job creation";
  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    const droppedFile = e.dataTransfer.files?.[0];
    if (droppedFile) onFileSelected(droppedFile);
  };

  return (
    <div>
      <div
        onDragOver={(e) => e.preventDefault()}
        onDrop={onDrop}
        className={cn(
          "flex flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed bg-muted/20 p-8 text-center transition-colors",
          phase === "error" && "border-destructive/50 bg-destructive/5",
        )}
      >
        {file ? (
          <div className="flex w-full flex-col items-center gap-2">
            <div className="flex items-center gap-2 text-sm">
              <FileText className="h-4 w-4 text-primary" />
              <span className="font-medium">{file.name}</span>
              <button
                type="button"
                onClick={onClear}
                className="rounded-full p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                aria-label="Remove file"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
            {phase === "uploading" && (
              <p className="flex items-center gap-2 text-xs text-muted-foreground">
                <Loader2 className="h-3 w-3 animate-spin" />
                Uploading…
              </p>
            )}
            {phase === "extracting" && (
              <p className="flex items-center gap-2 text-xs text-muted-foreground">
                <Sparkles className="h-3 w-3" />
                Reading the {instrumentLabel} with AI…
              </p>
            )}
            {phase === "done" && (
              <p className="flex items-center gap-2 text-xs text-emerald-600">
                <Check className="h-3 w-3" />
                Fields extracted.
              </p>
            )}
            {phase === "error" && (
              <p className="text-xs text-destructive">
                {error || "Something went wrong. Try again or skip."}
              </p>
            )}
          </div>
        ) : (
          <>
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
              <Upload className="h-5 w-5" />
            </div>
            <div className="flex flex-col gap-1">
              <p className="text-sm font-medium">{promptLabel}</p>
              <p className="text-xs text-muted-foreground">
                PDF or .docx, up to 25 MB
              </p>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
            >
              Choose file
            </Button>
          </>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,application/pdf,.docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
          className="hidden"
          onChange={(e) => {
            const selected = e.target.files?.[0];
            if (selected) onFileSelected(selected);
          }}
        />
      </div>
    </div>
  );
}
